import axios from 'axios';
import { normalizeUtcDates } from '../lib/normalizeDates';
import { reportConnectionStatus } from '../lib/connectionStatus';
import { readListMeta } from '../lib/listMeta';
import { reportTruncatedList } from '../lib/truncationWarning';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Retry configuration for 429 (Too Many Requests) errors
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000; // 1 second

// Helper function to delay execution
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Tracks whether the API is currently serving degraded (503 + X-Connection-Degraded)
// responses during a transient DB failover, so we only emit a connection-status
// change on transitions (not on every request).
let apiDegraded = false;

// Helper function to retry request with exponential backoff
const retryWithBackoff = async (requestFn, retries = MAX_RETRIES, delayMs = INITIAL_RETRY_DELAY) => {
  try {
    return await requestFn();
  } catch (error) {
    if (error.response?.status === 429 && retries > 0) {
      console.warn(`Rate limited (429). Retrying in ${delayMs}ms... (${retries} retries left)`);
      await delay(delayMs);
      return retryWithBackoff(requestFn, retries - 1, delayMs * 2); // Exponential backoff
    }
    throw error;
  }
};

const api = axios.create({
  baseURL: `${API_URL}/api`,
  // A request with no timeout does not fail, it hangs — and a hung page looks like a
  // broken one with nothing to tell the user. Heavy calls set their own longer value.
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Single-flight refresh: when multiple requests get a 401 around the same
// time we must NOT fire /auth/refresh more than once — the backend rotates
// refresh tokens, so a second concurrent call uses an invalidated token,
// fails, and would otherwise wipe everyone's session (logging out every
// open tab).
let refreshInFlight = null;

async function refreshOnce(prevAccessToken) {
  // If a sibling tab already rotated the token while we were waiting for the
  // cross-tab lock, just use the fresh one — calling /auth/refresh again with
  // the (already rotated) refresh token would 401 and log everyone out.
  const current = localStorage.getItem('accessToken');
  if (prevAccessToken && current && current !== prevAccessToken) {
    return current;
  }
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) throw new Error('No refresh token');
  const response = await axios.post(`${API_URL}/api/auth/refresh`, { refreshToken });
  const { accessToken, refreshToken: newRefreshToken } = response.data;
  localStorage.setItem('accessToken', accessToken);
  localStorage.setItem('refreshToken', newRefreshToken);
  return accessToken;
}

async function performTokenRefresh(prevAccessToken) {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    // Serialize refresh ACROSS TABS. Without this, two tabs of the same user
    // with an expired access token each refresh independently; the second uses
    // the just-rotated (now invalid) refresh token, fails, and wipes the
    // session for EVERY tab (the "random logout"). The Web Locks API guarantees
    // only one tab runs the refresh at a time; the others acquire the lock
    // afterwards and find the already-refreshed token in localStorage.
    if (navigator.locks && navigator.locks.request) {
      return navigator.locks.request('hal-token-refresh', () => refreshOnce(prevAccessToken));
    }
    return refreshOnce(prevAccessToken);
  })();
  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

// Response interceptor to handle token refresh and 429 retry
api.interceptors.response.use(
  (response) => {
    // Mark offset-less UTC datetimes with `Z` so the browser renders them in the
    // viewer's local timezone (fixes BST/GMT "1 hour out" on all timestamps).
    if (response && response.data) {
      response.data = normalizeUtcDates(response.data);
    }
    // A list that left rows behind says so in its headers. Warn once per endpoint
    // per session so no page can quietly filter or count a partial book — this is
    // the guard against the /billing 50-row class of bug appearing anywhere else.
    //
    // Only for callers that did NOT ask for a window: a page that sends `skip` is
    // paging deliberately and "there are more rows" is simply page 1 of several.
    // A call with no `skip` wanted the whole list and got part of it — that is the
    // dangerous case, and the only one worth interrupting anyone for.
    if (response?.headers?.['x-result-truncated'] === 'true') {
      const params = response.config?.params || {};
      const url = response.config?.url || '';
      const paging = params.skip != null || params.page != null
        || /[?&](skip|page)=/.test(url);
      // A caller that NAMED a limit chose its window knowingly — a "newest ten" card, a
      // type-ahead, a count probe. Telling him "showing 10 of 45 records" while he is
      // looking at a card headed "recent tickets" is noise, and it followed him around
      // every client and project page (his report, 31 Aug 2026). The dangerous case the
      // contract was written for is the caller that asked for EVERYTHING and got part of
      // it — no limit at all, or a limit the server had to cut down (which sets
      // `x-result-ceiling`).
      const namedLimit = params.limit != null || /[?&]limit=/.test(url);
      const serverClamped = response.headers['x-result-ceiling'] != null;
      if (!paging && (!namedLimit || serverClamped)) {
        reportTruncatedList(url, readListMeta(response));
      }
    }
    // Any successful response means the API is reachable again — clear a prior
    // degraded state so the top-bar "reconnecting" indicator hides.
    if (apiDegraded) {
      apiDegraded = false;
      reportConnectionStatus('api', false);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Global read resilience: a 503 carrying X-Connection-Degraded means the
    // backend hit a transient DB failover on a GET and served a degraded
    // response. Surface the subtle top-bar "reconnecting" indicator (once, on
    // transition) and let the error reject normally so pages keep their own
    // empty/error state.
    const degradedHeader = error.response?.headers?.['x-connection-degraded'];
    if (error.response?.status === 503 && (degradedHeader === '1' || error.response?.data?.degraded)) {
      if (!apiDegraded) {
        apiDegraded = true;
        reportConnectionStatus('api', true);
      }
    }
    
    // Handle 429 Too Many Requests with exponential backoff
    if (error.response?.status === 429 && !originalRequest._retryCount) {
      originalRequest._retryCount = 0;
    }
    
    if (error.response?.status === 429 && !originalRequest._noRetry429 && originalRequest._retryCount < MAX_RETRIES) {
      originalRequest._retryCount += 1;
      const retryDelay = INITIAL_RETRY_DELAY * Math.pow(2, originalRequest._retryCount - 1);
      console.warn(`Rate limited (429). Retry ${originalRequest._retryCount}/${MAX_RETRIES} in ${retryDelay}ms...`);
      await delay(retryDelay);
      return api(originalRequest);
    }
    
    // Handle 401 Unauthorized with token refresh.
    // NOTE: never run the refresh flow for the auth endpoints themselves. A 401
    // from /auth/login (wrong credentials) or /auth/refresh must propagate
    // untouched so the caller sees the real error (e.g. "Invalid email or
    // password"). Otherwise a failed login triggers performTokenRefresh(),
    // finds no refresh token, and throws a misleading "No refresh token" error
    // that masks the actual login failure.
    const requestUrl = originalRequest?.url || '';
    const isAuthEndpoint = /\/auth\/(login|refresh|logout|mfa\/(setup|verify))\b/.test(requestUrl);
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;

      // Before doing any work, check if another tab already refreshed the
      // access token while this request was in flight. If so, just retry
      // with whatever is currently in localStorage.
      const tokenAtRequestTime = originalRequest.headers?.Authorization?.replace(/^Bearer\s+/i, '');
      const currentToken = localStorage.getItem('accessToken');
      if (currentToken && currentToken !== tokenAtRequestTime) {
        originalRequest.headers.Authorization = `Bearer ${currentToken}`;
        return api(originalRequest);
      }

      try {
        const accessToken = await performTokenRefresh(tokenAtRequestTime);
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Final safety net: if a sibling tab refreshed successfully after
        // our refresh attempt failed (race window), prefer that token over
        // logging out.
        const latestToken = localStorage.getItem('accessToken');
        if (latestToken && latestToken !== tokenAtRequestTime) {
          originalRequest.headers.Authorization = `Bearer ${latestToken}`;
          return api(originalRequest);
        }
        // Remember where the user was so we can send them back after re-login.
        try {
          const path = window.location.pathname + window.location.search;
          if (path && !path.startsWith('/login') && !path.startsWith('/portal')) {
            localStorage.setItem('returnTo', path);
          }
        } catch (_) { /* ignore */ }
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

export default api;

// Auth API
export const authApi = {
  login: (email, password, trustedDeviceToken) => api.post('/auth/login', { email, password, trustedDeviceToken }),
  register: (data) => api.post('/auth/register', data),
  logout: (refreshToken) => api.post('/auth/logout', { refreshToken }),
  refresh: (refreshToken) => api.post('/auth/refresh', { refreshToken }),
  me: () => api.get('/auth/me'),
  changePassword: (currentPassword, newPassword) => 
    api.post('/auth/change-password', { currentPassword, newPassword }),
  unlockAccount: (userId) => api.post(`/auth/unlock-account/${userId}`),
  unlockPin: (userId) => api.post(`/auth/unlock-pin/${userId}`),
  listSessions: () => api.get('/auth/sessions'),
  revokeSession: (index) => api.delete(`/auth/sessions/${index}`),
  revokeOtherSessions: () => api.post('/auth/sessions/revoke-others'),
};

// Two-factor sign-in (v1.415.0)
export const mfaApi = {
  setup: (mfaToken) => api.post('/auth/mfa/setup', { mfaToken }),
  verify: (mfaToken, code, rememberDevice) => api.post('/auth/mfa/verify', { mfaToken, code, rememberDevice }),
  status: (trustedDeviceToken) => api.get('/auth/mfa/status', { headers: trustedDeviceToken ? { 'X-Trusted-Device': trustedDeviceToken } : {} }),
  regenerateBackupCodes: (code) => api.post('/auth/mfa/backup-codes', { code }),
  forgetDevice: (id) => api.delete(`/auth/mfa/trusted-devices/${id}`),
  forgetAllDevices: () => api.delete('/auth/mfa/trusted-devices'),
  setExempt: (userId, exempt) => api.put(`/auth/mfa/users/${userId}/exempt`, { exempt }),
  reset: (userId) => api.post(`/auth/mfa/users/${userId}/reset`),
};

// Users API
export const usersApi = {
  list: (params) => api.get('/users', { params }),
  get: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.patch(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  technicians: () => api.get('/users/technicians/list'),
  mentionable: (search) => api.get('/users/dropdown/mentionable', { params: { search } }),
  openAssignments: (id) => api.get(`/users/${id}/open-assignments`),
  reassignWork: (id, data) => api.post(`/users/${id}/reassign`, data),
};

// Teams API
export const teamsApi = {
  list: (params) => api.get('/teams', { params }),
  get: (id) => api.get(`/teams/${id}`),
  create: (data) => api.post('/teams', data),
  update: (id, data) => api.patch(`/teams/${id}`, data),
  delete: (id) => api.delete(`/teams/${id}`),
  dropdown: () => api.get('/teams/dropdown/list'),
  addMember: (teamId, userId, role = 'member') => 
    api.post(`/teams/${teamId}/members/${userId}`, null, { params: { role } }),
  updateMember: (teamId, userId, data) => 
    api.patch(`/teams/${teamId}/members/${userId}`, data),
  removeMember: (teamId, userId) => 
    api.delete(`/teams/${teamId}/members/${userId}`),
  getUserTeams: (userId) => api.get(`/teams/user/${userId}/teams`),
};

// Scheduled Blocks API (for Technician Scheduler)
export const scheduledBlocksApi = {
  list: (params) => api.get('/scheduled-blocks', { params }),
  get: (id) => api.get(`/scheduled-blocks/${id}`),
  create: (data) => api.post('/scheduled-blocks', data),
  update: (id, data) => api.patch(`/scheduled-blocks/${id}`, data),
  delete: (id) => api.delete(`/scheduled-blocks/${id}`),
  duplicate: (id, data) => api.post(`/scheduled-blocks/${id}/duplicate`, data),
  byItem: (itemType, itemId) => api.get(`/scheduled-blocks/by-item/${itemType}/${itemId}`),
  byUser: (userId, params) => api.get(`/scheduled-blocks/by-user/${userId}`, { params }),
  userStats: (userId, params) => api.get(`/scheduled-blocks/stats/user/${userId}`, { params }),
  scheduledItemIds: (params) => api.get('/scheduled-blocks/scheduled-item-ids', { params }),
};

// Clients API
export const clientsApi = {
  list: (params, config = {}) => api.get('/clients', { params, ...config }),
  get: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.patch(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  count: (params) => api.get('/clients/count', { params }),
  dropdown: () => api.get('/clients/dropdown/list'),
  resellers: () => api.get('/clients/resellers/list'),
  getChildren: (clientId) => api.get(`/clients/${clientId}/children`),
  vatTreatmentImpact: (clientId) => api.get(`/clients/${clientId}/vat-treatment-impact`),
  contacts: (clientId, params) => api.get(`/clients/${clientId}/contacts/list`, { params }),
  searchContacts: (query) => api.get('/clients/contacts/search', { params: { q: query } }),
  listAllContacts: (params, config = {}) => api.get('/clients/contacts/all', { params, ...config }),
  getAccountReviews: (params) => api.get('/clients/account-reviews', { params }),
  getContactReview: (clientId, contactId) => api.get(`/clients/${clientId}/contacts/${contactId}/review`),
  completeContactReview: (clientId, contactId, data) =>
    api.post(`/clients/${clientId}/contacts/${contactId}/review/complete`, data),
  addContact: (clientId, data) => api.post(`/clients/${clientId}/contacts`, data),
  updateContact: (clientId, contactId, data) => 
    api.patch(`/clients/${clientId}/contacts/${contactId}`, data),
  deleteContact: (clientId, contactId) => 
    api.delete(`/clients/${clientId}/contacts/${contactId}`),
  reactivateContact: (clientId, contactId) =>
    api.post(`/clients/${clientId}/contacts/${contactId}/reactivate`),
  setPrimaryContact: (clientId, contactId) =>
    api.put(`/clients/${clientId}/contacts/${contactId}/set-primary`),
  bulkUpdateUserType: (data) => api.post('/clients/contacts/bulk-update-user-type', data),
  bulkUpdateBillingEntity: (data) => api.post('/clients/contacts/bulk-update-billing-entity', data),
  // Sites API
  listSites: (clientId) => api.get(`/clients/${clientId}/sites`),
  getSite: (clientId, siteId) => api.get(`/clients/${clientId}/sites/${siteId}`),
  createSite: (clientId, data) => api.post(`/clients/${clientId}/sites`, data),
  updateSite: (clientId, siteId, data) => api.patch(`/clients/${clientId}/sites/${siteId}`, data),
  deleteSite: (clientId, siteId) => api.delete(`/clients/${clientId}/sites/${siteId}`),
  // Integration Health Dashboard
  // Integrations (M365 / NinjaOne / Xero) status for a client
  getIntegrationsStatus: (clientId) => api.get(`/clients/${clientId}/integrations-status`),
};

export const integrationHealthApi = {
  get: () => api.get('/integrations/health'),
};

// Site Photos API (site visit documentation photos)
export const sitePhotosApi = {
  list: (params) => api.get('/site-photos', { params }),
  get: (id) => api.get(`/site-photos/${id}`),
  siteCounts: (clientId) => api.get('/site-photos/site-counts', { params: { clientId } }),
  upload: (formData, onUploadProgress) =>
    api.post('/site-photos/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    }),
  update: (id, data) => api.patch(`/site-photos/${id}`, data),
  delete: (id) => api.delete(`/site-photos/${id}`),
  downloadOriginal: (originalUrl) =>
    api.get(originalUrl.replace(/^\/api/, ''), { responseType: 'blob' }),
};

// Assets API
export const assetsApi = {
  list: (params, config = {}) => api.get('/assets', { params, ...config }),
  billingEntityNames: (params) => api.get('/assets/billing-entity-names', { params }),
  get: (id) => api.get(`/assets/${id}`),
  create: (data) => api.post('/assets', data),
  update: (id, data) => api.patch(`/assets/${id}`, data),
  revealSecret: (id, fieldName) => api.post(`/assets/${id}/secrets/${encodeURIComponent(fieldName)}/reveal`),
  byClient: (clientId) => api.get(`/assets/client/${clientId}/list`),
  byContact: (contactId) => api.get(`/assets/contact/${contactId}/list`),
  // Moving a hand-created asset to a different client (clears the old client's links)
  moveClientPreview: (id) => api.get(`/assets/${id}/move-client/preview`),
  moveClient: (id, data) => api.post(`/assets/${id}/move-client`, data),
  // Topology
  getTopology: (clientId, params) => api.get(`/assets/client/${clientId}/topology`, { params }),
  // Relationships
  addRelationship: (assetId, data) => api.post(`/assets/${assetId}/relationships`, data),
  updateRelationship: (assetId, relationshipId, data) => api.patch(`/assets/${assetId}/relationships/${relationshipId}`, data),
  deleteRelationship: (assetId, relationshipId) => api.delete(`/assets/${assetId}/relationships/${relationshipId}`),
  // Attachments
  uploadAttachment: (assetId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/assets/${assetId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteAttachment: (assetId, attachmentId) => api.delete(`/assets/${assetId}/attachments/${attachmentId}`),
  downloadAttachment: (assetId, attachmentId) => `${api.defaults.baseURL}/assets/${assetId}/attachments/${attachmentId}/download`,
  bulkUpdate: (data) => api.post('/assets/bulk/update', data),
  previewRetiredCleanup: (clientId) => api.get('/assets/cleanup/retired/preview', { params: { clientId } }),
  applyRetiredCleanup: (clientId) => api.post('/assets/cleanup/retired/apply', { clientId }),
  previewDuplicateCleanup: (clientId) => api.get('/assets/cleanup/duplicates/preview', { params: { clientId } }),
  applyDuplicateCleanup: (clientId) => api.post('/assets/cleanup/duplicates/apply', { clientId }),
  renewalReview: (params) => api.get('/assets/service-linked/renewal-review', { params }),
  getResourceAccountLinkedAsset: (contactId) => api.get(`/assets/resource-account/${contactId}/linked-asset`),
  servicesOverviewCategories: () => api.get('/assets/services-overview/categories'),
  servicesOverview: (category) => api.get('/assets/services-overview', { params: { category } }),
};

// Saved Views API (per-user list configs)
export const savedViewsApi = {
  list: (scope) => api.get('/saved-views', { params: { scope } }),
  create: (data) => api.post('/saved-views', data),
  update: (id, data) => api.put(`/saved-views/${id}`, data),
  remove: (id) => api.delete(`/saved-views/${id}`),
  clear: (scope) => api.delete('/saved-views', { params: { scope } }),
};


// Tickets API
export const ticketsApi = {
  list: (params, config = {}) => api.get('/tickets', { params, ...config }),
  recentHistory: (params) => api.get('/tickets/recent-history', { params }),
  get: (id) => api.get(`/tickets/${id}`),
  create: (data) => api.post('/tickets', data),
  update: (id, data) => api.patch(`/tickets/${id}`, data),
  setNonBillable: (id, nonBillable) => api.patch(`/tickets/${id}/billable-status`, { nonBillable }),
  delete: (id) => api.delete(`/tickets/${id}`),
  bulkUpdate: (data) => api.post('/tickets/bulk/update', data),
  bulkDelete: (ticketIds) => api.post('/tickets/bulk/delete', ticketIds),
  changeCreatedDate: (id, data) => api.patch(`/tickets/${id}/created-date`, data),
  count: (params) => api.get('/tickets/count', { params }),
  downloadPdf: (id, params) =>
    api.get(`/tickets/${id}/pdf`, { params, responseType: 'blob', timeout: 120000 }),  addTodo: (id, description) => 
    api.post(`/tickets/${id}/todos?description=${encodeURIComponent(description)}`),
  updateTodo: (id, todoId, completed) => 
    api.patch(`/tickets/${id}/todos/${todoId}?completed=${completed}`),
  renameTodo: (id, todoId, description) =>
    api.patch(`/tickets/${id}/todos/${todoId}?description=${encodeURIComponent(description)}`),
  deleteTodo: (id, todoId) => api.delete(`/tickets/${id}/todos/${todoId}`),
  reorderTodos: (id, todoIds) => api.put(`/tickets/${id}/todos/reorder`, todoIds),
  getCommunications: (id) => api.get(`/tickets/${id}/communications`),
  addCommunication: (id, data) => api.post(`/tickets/${id}/communications`, data),
  updateCommunication: (ticketId, commId, data) => api.patch(`/tickets/${ticketId}/communications/${commId}`, data),
  getChildren: (id) => api.get(`/tickets/${id}/children`),
  family: (id) => api.get(`/tickets/${id}/family`),
  getDepth: (id) => api.get(`/tickets/${id}/depth`),
  // Billable items - Phase 2F
  listBillableItems: (ticketId, params) => api.get(`/tickets/${ticketId}/billable-items`, { params }),
  addBillableItem: (ticketId, data) => api.post(`/tickets/${ticketId}/billable-items`, data),
  updateBillableItem: (ticketId, itemId, data) => api.patch(`/tickets/${ticketId}/billable-items/${itemId}`, data),
  setBillableItemStatus: (ticketId, itemId, billableToClient) => api.patch(`/tickets/${ticketId}/billable-items/${itemId}/billable-status`, { billableToClient }),
  removeBillableItem: (ticketId, itemId) => api.delete(`/tickets/${ticketId}/billable-items/${itemId}`),
  // Pending items admin view
  listAllPendingItems: (params) => api.get('/tickets/pending-items', { params }),
  getPendingItemsSummary: () => api.get('/tickets/pending-items/summary'),
  // Queue pending items to review
  queuePendingItemsToReview: (itemIds) => api.post('/tickets/pending-items/queue-to-review', itemIds ? { itemIds } : null),
  createInvoiceFromPendingItems: (reviewId, payload = {}) => api.post(`/tickets/pending-items/create-invoice/${reviewId}`, payload),
  listInvoiceReferenceTemplates: (usageContext) => api.get('/tickets/invoice-reference-templates', { params: usageContext ? { usageContext } : {} }),
  // Snooze
  snooze: (id, data) => api.post(`/tickets/${id}/snooze`, data),
  convertToProject: (id, data) => api.post(`/tickets/${id}/convert-to-project`, data),
  unsnooze: (id) => api.post(`/tickets/${id}/unsnooze`),
  togglePin: (id) => api.post(`/tickets/${id}/pin`),
  getPinStatus: (id) => api.get(`/tickets/${id}/pin-status`),
  getPinned: () => api.get('/tickets/pinned/list'),
  merge: (primaryId, sourceTicketIds) => api.post(`/tickets/${primaryId}/merge`, { sourceTicketIds }),
  dismissDuplicate: (id) => api.post(`/tickets/${id}/dismiss-duplicate`),
  moveNote: (ticketId, noteId, targetTicketId) => api.post(`/tickets/${ticketId}/move-note/${noteId}`, { targetTicketId }),
  generateResolution: (ticketId) => api.post(`/tickets/${ticketId}/generate-resolution`, null, { _noRetry429: true }),
  getAiPromptSettings: () => api.get('/tickets/ai-prompt-settings'),
  updateAiPromptSettings: (data) => api.put('/tickets/ai-prompt-settings', data),
  resetAiPromptSettings: () => api.post('/tickets/ai-prompt-settings/reset'),
  // Follow
  getFollowers: (ticketId) => api.get(`/tickets/${ticketId}/followers`),
  toggleFollow: (ticketId) => api.post(`/tickets/${ticketId}/follow`),
  updateFollowers: (ticketId, followerIds) => api.put(`/tickets/${ticketId}/followers`, { followerIds }),
  // People / Participants (Batch 1)
  getPeople: (ticketId) => api.get(`/tickets/${ticketId}/people`),
  getByContact: (contactId, clientId) => api.get(`/tickets/by-contact/${contactId}`, { params: { client_id: clientId } }),
  addParticipant: (ticketId, data) => api.post(`/tickets/${ticketId}/participants`, data),
  updateParticipant: (ticketId, participantId, data) => api.patch(`/tickets/${ticketId}/participants/${participantId}`, data),
  removeParticipant: (ticketId, participantId) => api.delete(`/tickets/${ticketId}/participants/${participantId}`),
  acceptSuggestion: (ticketId, suggestionId) => api.post(`/tickets/${ticketId}/suggestions/${suggestionId}/accept`),
  dismissSuggestion: (ticketId, suggestionId) => api.post(`/tickets/${ticketId}/suggestions/${suggestionId}/dismiss`),
};

// Time Entries API
// Health Checks (DB hygiene) admin tools
export const healthChecksApi = {
  scanContactEmails: () => api.get('/health-checks/contact-emails/scan'),
  normaliseContactEmails: () => api.post('/health-checks/contact-emails/normalise'),
  normaliseContactEmailsForClient: (clientId) =>
    api.post(`/health-checks/contact-emails/normalise/${clientId}`),
  scanOrphanedData: (includeReviewed = false) => api.get('/health-checks/orphaned-data/scan', { params: { include_reviewed: includeReviewed } }),
  getOrphanRecord: (collection, id) => api.get(`/health-checks/orphaned-data/record/${collection}/${id}`),
  deleteOrphans: (collection, ids) => api.post('/health-checks/orphaned-data/delete', { collection, ids }),
  reassignOrphans: (collection, ids, newClientId) => api.post('/health-checks/orphaned-data/reassign', { collection, ids, newClientId }),
  markOrphansReviewed: (collection, ids) => api.post('/health-checks/orphaned-data/mark-reviewed', { collection, ids }),
  scanContactsWithoutSite: () => api.get('/health-checks/contacts-without-site/scan'),
  fixContactsWithoutSite: () => api.post('/health-checks/contacts-without-site/fix'),
  scanDuplicateDomains: () => api.get('/health-checks/duplicate-domains/scan'),
  scanM365Domains: () => api.get('/health-checks/m365-domains/scan'),
  scanM365ContactStatus: () => api.get('/health-checks/m365-contact-status/scan'),
  fixM365ContactStatus: () => api.post('/health-checks/m365-contact-status/fix'),
  scanCatchallContacts: () => api.get('/health-checks/catchall-contacts/scan'),
  fixCatchallContacts: () => api.post('/health-checks/catchall-contacts/fix'),
  scanDataIntegrity: () => api.get('/health-checks/data-integrity/scan'),
  previewNormalizeField: (collection, field) => api.post('/health-checks/data-integrity/normalize/preview', { collection, field }),
  applyNormalizeField: (collection, field) => api.post('/health-checks/data-integrity/normalize/apply', { collection, field }),
  normalizeAllFixable: () => api.post('/health-checks/data-integrity/normalize/all'),
  scanTicketNumbering: () => api.get('/health-checks/ticket-numbering/scan'),
  consolidateTicketNumbering: () => api.post('/health-checks/ticket-numbering/consolidate'),
  scanContractBilling: () => api.get('/health-checks/contract-billing/scan'),
  contractEntityPlan: () => api.get('/health-checks/contract-billing/entity-plan'),
  stampContractEntities: () => api.post('/health-checks/contract-billing/stamp-entities'),
  monthlyValuePlan: () => api.get('/health-checks/contract-billing/monthly-value-plan'),
  restampMonthlyValues: () => api.post('/health-checks/contract-billing/restamp-monthly-values'),
  taxRatePlan: () => api.get('/health-checks/contract-billing/tax-rate-plan'),
  fixTaxRates: (data) => api.post('/health-checks/contract-billing/fix-tax-rates', data),
  clientBillingPlan: () => api.get('/health-checks/client-billing/plan'),
  clientBillingBackfill: () => api.post('/health-checks/client-billing/backfill'),
  scanBillingCadence: (months = 12) =>
    api.get('/health-checks/billing-cadence/scan', { params: { months } }),
  scanLineLinks: () => api.get('/health-checks/line-links/scan'),
  backfillLineLinks: () => api.post('/health-checks/line-links/backfill'),
  scanTimeContractLinks: () => api.get('/health-checks/time-contract-links/scan'),
  backfillTimeContractLinks: () => api.post('/health-checks/time-contract-links/backfill'),
  scanInternalAssets: () => api.get('/health-checks/internal-assets/scan'),
  fixInternalAssets: () => api.post('/health-checks/internal-assets/fix'),
  scanCoreHardware: () => api.get('/health-checks/core-hardware/scan'),
  backfillCoreHardware: () => api.post('/health-checks/core-hardware/backfill'),
  getSelfHealLog: (limit = 200) => api.get('/health-checks/self-heal-log', { params: { limit } }),
  scanDanglingRefs: () => api.get('/health-checks/dangling-refs/scan'),
  fixDanglingRefs: (data) => api.post('/health-checks/dangling-refs/fix', data),
  scanRetainerTerms: () => api.get('/health-checks/retainer-terms/scan'),
  fixRetainerTerms: (data) => api.post('/health-checks/retainer-terms/fix', data),
  scanLinkMisses: () => api.get('/health-checks/link-misses/scan'),
  clearLinkMisses: (data) => api.post('/health-checks/link-misses/clear', data),
  getLinkMissDigest: () => api.get('/health-checks/link-misses/digest'),
  setLinkMissDigest: (data) => api.patch('/health-checks/link-misses/digest', data),
  sendLinkMissDigestNow: () => api.post('/health-checks/link-misses/digest/send-now'),
};

// The 404 pages report the address that led nowhere. Fire and forget — see
// hooks/useReportLinkMiss.js.
export const linkMissesApi = {
  report: (data) => api.post('/link-misses', data),
  reportPortal: (data) => api.post('/portal/link-misses', data),
};

export const billingIntegrityApi = {
  scan: () => api.get('/billing-integrity/scan'),
  restoreInvoiceProtection: () => api.post('/billing-integrity/restore-invoice-protection'),
  unlockOrphanedInvoiced: () => api.post('/billing-integrity/unlock-orphaned-invoiced'),
  getInvoiceLineRepairs: () => api.get('/billing-integrity/invoice-line-repairs'),
  applyInvoiceLineRepair: (body) => api.post('/billing-integrity/invoice-line-repairs/apply', body),
  backfillCourierVat: () => api.post('/billing-integrity/backfill-courier-vat'),
  // Ledger migration phase 1 — mode: 'dry' | 'apply' | 'revert'.
  ledgerBackfill: (kind, mode) => api.post(`/billing-integrity/${kind}`, null, {
    params: { apply: mode === 'apply', revert: mode === 'revert' },
  }),
  // Ledger migration phase 2.1 — the projectLedgerReads switch.
  getLedgerReads: () => api.get('/billing-integrity/ledger-reads'),
  setLedgerReads: (enabled) => api.put('/billing-integrity/ledger-reads', { enabled }),
  verificationReport: () => api.get('/billing-integrity/verification-report'),
  runVerificationReport: () => api.post('/billing-integrity/verification-report'),
  downloadVerificationReport: (name) => api.get('/billing-integrity/verification-report/download', {
    params: { name }, responseType: 'blob', timeout: 120000,
  }),
  cleanupDuplicateInvoices: () => api.post('/billing-integrity/cleanup-duplicate-invoices'),
  summary: (force = false) => api.get('/billing-integrity/summary', { params: { force } }),
  getBannerSetting: () => api.get('/billing-integrity/banner-setting'),
  setBannerSetting: (enabled) => api.put('/billing-integrity/banner-setting', { enabled }),
  listZeroAmount: (params) => api.get('/billing-integrity/zero-amount-billable', { params }),
  listWrongCompanyRate: (params) => api.get('/billing-integrity/wrong-company-rate', { params }),
  applyWrongCompanyRate: (items) => api.post('/billing-integrity/wrong-company-rate/apply', { items }),
  verifyXeroPush: () => api.post('/billing-integrity/verify-xero-push'),
  applyZeroAmount: (items) => api.post('/billing-integrity/zero-amount-billable/apply', { items }),
  applyAllSuggestedZeroAmount: (clientId) => api.post('/billing-integrity/zero-amount-billable/apply-all-suggested', null, { params: clientId ? { client_id: clientId } : {} }),
  listOrphanedItems: (params) => api.get('/billing-integrity/orphaned-billable-items', { params }),
  applyOrphanedItems: (items) => api.post('/billing-integrity/orphaned-billable-items/apply', { items }),
  listRateWindowRepairs: (params) => api.get('/billing-integrity/rate-window-repair', { params }),
  applyRateWindowRepairs: (rateIds) => api.post('/billing-integrity/rate-window-repair/apply', { rateIds }),
  listCreditLineVat: () => api.get('/billing-integrity/credit-line-vat'),
  applyCreditLineVat: (invoiceIds) => api.post('/billing-integrity/credit-line-vat/apply', { invoiceIds }),
  listLineVatNaming: () => api.get('/billing-integrity/line-vat-naming'),
  applyLineVatNaming: (invoiceIds) => api.post('/billing-integrity/line-vat-naming/apply', { invoiceIds }),
  listRateDisagreements: () => api.get('/billing-integrity/rate-disagreement'),
  applyRateDisagreements: (timeEntryIds) => api.post('/billing-integrity/rate-disagreement/apply', { timeEntryIds }),
  listStaleSchedules: () => api.get('/billing-integrity/schedule-repair'),
  applyStaleSchedules: (contractIds) => api.post('/billing-integrity/schedule-repair/apply', { contractIds }),
  // The scan reads the whole book through 28 checks, which outgrew the request
  // timeout — so it is started and then polled (12 Sep 2026).
  startScan: () => api.post('/billing-integrity/scan/start'),
  latestScan: () => api.get('/billing-integrity/scan/latest'),
  // "I know about that one, and it's deliberate" — only the two HAL-vs-Xero findings.
  acknowledge: (checkId, invoiceId, reason) => api.post('/billing-integrity/acknowledge', { checkId, invoiceId, reason }),
  unacknowledge: (checkId, invoiceId) => api.delete(`/billing-integrity/acknowledge/${checkId}/${invoiceId}`),
};

export const billingPresendApi = {
  review: (clientId) => api.get('/billing/presend/review', { params: clientId ? { clientId } : {} }),
  restampPreview: (invoiceId) => api.get(`/billing/presend/restamp/${invoiceId}`),
  restamp: (invoiceId) => api.post('/billing/presend/restamp', { invoiceId }),
};

export const billingDiagnosticsApi = {
  timeMapping: (params) => api.get('/billing-diagnostics/time-mapping', { params }),
  zeroPriced: (params) => api.get('/billing-diagnostics/zero-priced', { params }),
  invoicesMissingLines: (params) => api.get('/billing-diagnostics/invoices-missing-lines', { params }),
};

export const billingAuditApi = {
  // Full runs take minutes — no client timeout.
  run: (sections) => api.get('/billing-audit/report', {
    params: sections && sections.length ? { sections: sections.join(',') } : {},
    timeout: 0,
  }),
  latest: () => api.get('/billing-audit/latest'),
  history: (limit = 20) => api.get('/billing-audit/history', { params: { limit } }),
  dismiss: (body) => api.post('/billing-audit/dismissals', body),
  restore: (key) => api.delete(`/billing-audit/dismissals/${encodeURIComponent(key)}`),
};

export const timeEntriesApi = {
  list: (params) => api.get('/time-entries', { params }),
  get: (id) => api.get(`/time-entries/${id}`),
  create: (data) => api.post('/time-entries', data),
  update: (id, data) => api.patch(`/time-entries/${id}`, data),
  delete: (id) => api.delete(`/time-entries/${id}`),
  summary: (params) => api.get('/time-entries/summary', { params }),
  report: (params) => api.get('/time-entries/report', { params }),
  dailyBreakdown: (params) => api.get('/time-entries/daily-breakdown', { params }),
  teamDaily: (params) => api.get('/time-entries/team-daily', { params }),
  // Get unbilled time entries for a ticket
  getUnbilledByTicket: (ticketId) => api.get(`/time-entries/ticket/${ticketId}/unbilled`),
  // Get all pending (unbilled) time entries
  listPending: (params) => api.get('/time-entries/pending', { params }),
  // Move a time entry to a different ticket or project.
  // `target` = { type: 'ticket'|'project', id } (legacy: a bare ticket id string)
  move: (id, target) => api.post(
    `/time-entries/${id}/move`,
    typeof target === 'string'
      ? { targetTicketId: target }
      : { targetType: target.type, targetId: target.id }
  ),
  // Timer cleanup admin tool
  cleanupReport: () => api.get('/time-entries/cleanup-report'),
  cleanupDelete: (entryIds, force = false) => api.post('/time-entries/cleanup-delete', { entryIds, force }),
  assignContract: (entryIds, contractId, applyToTicketEntity = false) =>
    api.post('/time-entries/assign-contract', { entryIds, contractId, applyToTicketEntity }),
  resolveRate: (clientId, context = {}) => api.get('/time-entries/resolve-rate', { params: { clientId, ...context } }),
};

// Notes API (unified across tickets & projects)
export const notesApi = {
  // payload: { sourceType, sourceId, noteId, targetType, targetId }
  move: (payload) => api.post('/notes/move', payload),
};

// Settings API
export const settingsApi = {
  list: (params) => api.get('/settings', { params }),
  byCategory: (category, isActive = true) => 
    api.get(`/settings/by-category/${category}`, { params: { isActive } }),
  get: (id) => api.get(`/settings/${id}`),
  create: (data) => api.post('/settings', data),
  update: (id, data) => api.patch(`/settings/${id}`, data),
  delete: (id) => api.delete(`/settings/${id}`),
  seed: () => api.post('/settings/seed'),
  reorder: (category, settingIds) => 
    api.post('/settings/reorder', settingIds, { params: { category } }),
  categories: () => api.get('/settings/categories'),
};

// One id per browser tab (per page load, so a duplicated tab gets its own). A timer is shared
// by every tab that has its ticket/project open and only goes when the last of them leaves.
export const TAB_ID = (typeof crypto !== 'undefined' && crypto.randomUUID)
  ? crypto.randomUUID()
  : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const timerItemParams = ({ ticketId, projectId } = {}) => ({
  ...(ticketId ? { ticket_id: ticketId } : {}),
  ...(projectId ? { project_id: projectId } : {}),
});

// Timer API
export const timerApi = {
  start: (clientId, params = {}) => {
    const queryParams = new URLSearchParams({ client_id: clientId, tab_id: TAB_ID });
    if (params.ticketId) queryParams.append('ticket_id', params.ticketId);
    if (params.projectId) queryParams.append('project_id', params.projectId);
    if (params.description) queryParams.append('description', params.description);
    if (params.workType) queryParams.append('work_type', params.workType);
    if (params.autoStopExisting) queryParams.append('auto_stop_existing', 'true');
    // After Send: swap this ticket/project's clock for a fresh one in one step, keeping its tabs.
    if (params.restart) queryParams.append('restart', 'true');
    return api.post(`/time-entries/timer/start?${queryParams}`);
  },
  // Stop always discards — time is never auto-saved from a timer.
  // The only persistence path is the Activity Composer "Send" button.
  // `entryId` scopes the cancel to one timer. `leave: true` is a page closing: the timer is
  // only discarded if no other tab still has it open. Without it (Stop pressed) it goes for all tabs.
  cancel: (entryId, { leave = false } = {}) => api.delete('/time-entries/timer/cancel', {
    params: entryId ? { entry_id: entryId, ...(leave ? { tab_id: TAB_ID } : {}) } : undefined,
  }),
  /**
   * Leave-cancel during page unload, when axios is too late to be reliable.
   * `keepalive` lets the request outlive the page; `sendBeacon` cannot be used because
   * cancelling is a DELETE.
   */
  cancelOnUnload: (entryId) => {
    const token = localStorage.getItem('accessToken');
    if (!token || typeof fetch !== 'function') return;
    const query = entryId ? `?entry_id=${encodeURIComponent(entryId)}&tab_id=${TAB_ID}` : '';
    try {
      fetch(`${API_URL}/api/time-entries/timer/cancel${query}`, {
        method: 'DELETE',
        keepalive: true,
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    } catch {
      // The page is going away — nothing useful to report.
    }
  },
  active: (item) => api.get('/time-entries/timer/active', { params: timerItemParams(item) }),
  // "This tab still has the page open" — returns the shared clock for that ticket/project, or null.
  heartbeat: (item) => api.post('/time-entries/timer/heartbeat', null, { params: { tab_id: TAB_ID, ...timerItemParams(item) } }),
  pause: (entryId) => api.post('/time-entries/timer/pause', null, { params: entryId ? { entry_id: entryId } : undefined }),
  resume: (entryId) => api.post('/time-entries/timer/resume', null, { params: entryId ? { entry_id: entryId } : undefined }),
};

// Projects API
export const projectsApi = {
  list: (params, config = {}) => api.get('/projects', { params, ...config }),
  get: (id) => api.get(`/projects/${id}`),
  getTimeSummary: (id) => api.get(`/projects/${id}/time-summary`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.patch(`/projects/${id}`, data),
  setNonBillable: (id, nonBillable) => api.patch(`/projects/${id}/billable-status`, { nonBillable }),
  delete: (id) => api.delete(`/projects/${id}`),
  dropdown: (clientId) => api.get('/projects/dropdown/list', { params: { clientId } }),
  billingContext: (clientId) => api.get(`/projects/billing-context/${clientId}`),
  quotedVsActual: (id) => api.get(`/projects/${id}/quoted-vs-actual`),
  capChangePreview: (id, budgetHours) =>
    api.get(`/projects/${id}/cap-change-preview`, { params: { budgetHours } }),
  budgetChangePreview: (id, budgetHours, budgetAmount) =>
    api.get(`/projects/${id}/budget-change-preview`, { params: { budgetHours, budgetAmount } }),
  billingTypeChangePreview: (id, params) =>
    api.get(`/projects/${id}/billing-type-change-preview`, { params }),
  billingTypeChange: (id, data) => api.post(`/projects/${id}/billing-type-change`, data),
  downloadPdf: (id, params) =>
    api.get(`/projects/${id}/pdf`, { params, responseType: 'blob', timeout: 120000 }),
  addMilestone: (id, data) => api.post(`/projects/${id}/milestones`, data),
  updateMilestone: (id, milestoneId, status) => 
    api.patch(`/projects/${id}/milestones/${milestoneId}?milestone_status=${status}`),
  editMilestone: (id, milestoneId, data) =>
    api.put(`/projects/${id}/milestones/${milestoneId}`, data),
  reorderMilestones: (id, milestoneIds) =>
    api.post(`/projects/${id}/milestones/reorder`, milestoneIds),
  deleteMilestone: (id, milestoneId) =>
    api.delete(`/projects/${id}/milestones/${milestoneId}`),
  // Todos
  addTodo: (id, data) => api.post(`/projects/${id}/todos`, data),
  updateTodo: (id, todoId, data) => api.patch(`/projects/${id}/todos/${todoId}`, data),
  deleteTodo: (id, todoId) => api.delete(`/projects/${id}/todos/${todoId}`),
  reorderTodos: (id, todoIds) => api.post(`/projects/${id}/todos/reorder`, todoIds),
  // Task workflow selector on the project
  setTaskWorkflow: (id, taskWorkflowId) => api.patch(`/projects/${id}`, { taskWorkflowId }),
  // Followers
  getFollowers: (id) => api.get(`/projects/${id}/followers`),
  toggleFollow: (id) => api.post(`/projects/${id}/follow`),
  updateFollowers: (id, followerIds) => api.put(`/projects/${id}/followers`, { followerIds }),
  // Communications/Notes
  getCommunications: (id) => api.get(`/projects/${id}/communications`),
  addCommunication: (id, data) => api.post(`/projects/${id}/communications`, data),
  updateCommunication: (id, commId, data) => api.patch(`/projects/${id}/communications/${commId}`, data),
  deleteCommunication: (id, commId) => api.delete(`/projects/${id}/communications/${commId}`),
  // People / Participants (Batch 2)
  getPeople: (id) => api.get(`/projects/${id}/people`),
  addParticipant: (id, data) => api.post(`/projects/${id}/participants`, data),
  updateParticipant: (id, participantId, data) => api.patch(`/projects/${id}/participants/${participantId}`, data),
  removeParticipant: (id, participantId) => api.delete(`/projects/${id}/participants/${participantId}`),
  acceptSuggestion: (id, suggestionId) => api.post(`/projects/${id}/suggestions/${suggestionId}/accept`),
  dismissSuggestion: (id, suggestionId) => api.post(`/projects/${id}/suggestions/${suggestionId}/dismiss`),
  getByContact: (contactId, clientId) => api.get(`/projects/by-contact/${contactId}`, { params: { client_id: clientId } }),
  // Assets
  getAssets: (id) => api.get(`/projects/${id}/assets`),
  linkAssets: (id, assetIds) => api.post(`/projects/${id}/assets`, { assetIds }),
  unlinkAsset: (id, assetId) => api.delete(`/projects/${id}/assets/${assetId}`),
  // Attachments
  getAttachments: (id) => api.get(`/projects/${id}/attachments`),
  uploadAttachment: (id, formData) => api.post(`/projects/${id}/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteAttachment: (id, attachmentId) => api.delete(`/projects/${id}/attachments/${attachmentId}`),
  // Billable Items (Stock)
  listBillableItems: (id, status) => api.get(`/projects/${id}/billable-items`, { params: { status } }),
  addBillableItem: (id, data) => api.post(`/projects/${id}/billable-items`, data),
  updateBillableItem: (id, itemId, data) => api.patch(`/projects/${id}/billable-items/${itemId}`, data),
  setBillableItemStatus: (id, itemId, billableToClient) => api.patch(`/projects/${id}/billable-items/${itemId}/billable-status`, { billableToClient }),
  removeBillableItem: (id, itemId) => api.delete(`/projects/${id}/billable-items/${itemId}`),
};

// Project Tasks API (rich tasks that replace the legacy embedded todos)
export const projectTasksApi = {
  meta: (projectId) => api.get(`/projects/${projectId}/tasks/meta`),
  list: (projectId, params) => api.get(`/projects/${projectId}/tasks`, { params }),
  create: (projectId, data) => api.post(`/projects/${projectId}/tasks`, data),
  get: (projectId, taskId) => api.get(`/projects/${projectId}/tasks/${taskId}`),
  update: (projectId, taskId, data) => api.patch(`/projects/${projectId}/tasks/${taskId}`, data),
  remove: (projectId, taskId) => api.delete(`/projects/${projectId}/tasks/${taskId}`),
  reorder: (projectId, containerKey, taskIds) =>
    api.post(`/projects/${projectId}/tasks/reorder`, { containerKey, taskIds }),
  // Comments
  listComments: (projectId, taskId) => api.get(`/projects/${projectId}/tasks/${taskId}/comments`),
  addComment: (projectId, taskId, body) => api.post(`/projects/${projectId}/tasks/${taskId}/comments`, { body }),
  deleteComment: (projectId, taskId, commentId) => api.delete(`/projects/${projectId}/tasks/${taskId}/comments/${commentId}`),
  // Attachments
  uploadAttachment: (projectId, taskId, formData) => api.post(`/projects/${projectId}/tasks/${taskId}/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  deleteAttachment: (projectId, taskId, attachmentId) => api.delete(`/projects/${projectId}/tasks/${taskId}/attachments/${attachmentId}`),
  // Cross-project ("My tasks / open bugs")
  listAll: (params) => api.get('/tasks', { params }),
};

// Audit Logs API
export const auditApi = {
  list: (params) => api.get('/audit', { params }),
  entityHistory: (entityType, entityId, limit = 50) => 
    api.get(`/audit/entity/${entityType}/${entityId}`, { params: { limit } }),
  userActivity: (userId, params = {}) => api.get(`/audit/user/${userId}`, { params }),
  clientActivity: (clientId, limit = 50) => 
    api.get(`/audit/client/${clientId}`, { params: { limit } }),
  summary: (params = {}) => api.get('/audit/summary', { params }),
  entityTimeline: (entityType, entityId, skip = 0, limit = 20) =>
    api.get(`/audit/entity/${entityType}/${entityId}/timeline`, { params: { skip, limit } }),
};

// Email Logs API
export const emailLogsApi = {
  list: (params) => api.get('/email-logs', { params }),
  getTypes: () => api.get('/email-logs/types'),
  getStats: (sinceHours = 24) => api.get('/email-logs/stats', { params: { since_hours: sinceHours } }),
  clearOld: (olderThanDays = 30) => api.delete('/email-logs/clear', { params: { older_than_days: olderThanDays } }),
  duplicateStats: (days = 30) => api.get('/email-logs/duplicate-stats', { params: { days } }),
};

// Attachments an inbound email carried that could not be imported — retried automatically,
// and dismissible once he has decided one is never coming back.
export const skippedAttachmentsApi = {
  list: (params) => api.get('/email/skipped-attachments', {
    params: { include_dismissed: params?.includeDismissed, include_recovered: params?.includeRecovered },
  }),
  retry: (id) => api.post(`/email/skipped-attachments/${id}/retry`),
  dismiss: (id) => api.post(`/email/skipped-attachments/${id}/dismiss`),
};

// Email thread diagnostics (RCA for inbound-reply threading)
export const emailDiagnosticsApi = {
  thread: (ticket) => api.get('/email-diagnostics/thread', { params: { ticket } }),
};

// Notifications API
export const notificationsApi = {
  list: (params) => api.get('/notifications', { params }),
  getTypes: () => api.get('/notifications/types'),
  get: (id) => api.get(`/notifications/${id}`),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id) => api.patch(`/notifications/${id}/read`),
  markAsUnread: (id) => api.patch(`/notifications/${id}/unread`),
  delete: (id) => api.delete(`/notifications/${id}`),
  bulkDelete: (ids) => api.post('/notifications/bulk-delete', { ids }),
  markAllAsRead: () => api.post('/notifications/mark-all-read'),
  deleteAll: (params) => api.delete('/notifications/delete-all', { params }),
  getPreferences: () => api.get('/notifications/preferences'),
  updatePreferences: (data) => api.put('/notifications/preferences', data),
  getInAppTemplates: () => api.get('/notifications/inapp-templates'),
  updateInAppTemplates: (data) => api.put('/notifications/inapp-templates', data),
  getCatalogue: () => api.get('/notifications/catalogue'),
  getAudience: () => api.get('/notifications/audience'),
  setUserPreference: (userId, data) => api.patch(`/notifications/users/${userId}/preferences`, data),
  setRequiredTypes: (types) => api.put('/notifications/required', { types }),
};

// Company Activity ("Company Pulse") API
export const activityApi = {
  company: (params) => api.get('/activity/company', { params }),
};

// Scheduled Tasks API
export const scheduledTasksApi = {
  list: (params) => api.get('/scheduled-tasks', { params }),
  calendar: (params) => api.get('/scheduled-tasks/calendar', { params }),
  get: (id) => api.get(`/scheduled-tasks/${id}`),
  create: (data) => api.post('/scheduled-tasks', data),
  update: (id, data) => api.patch(`/scheduled-tasks/${id}`, data),
  delete: (id) => api.delete(`/scheduled-tasks/${id}`),
  run: (id) => api.post(`/scheduled-tasks/${id}/run`),
  duplicate: (id) => api.post(`/scheduled-tasks/${id}/duplicate`),
  previewRuns: (data) => api.post('/scheduled-tasks/preview-runs', data),
  processDue: () => api.post('/scheduled-tasks/process-due'),
};

// Roadmap (Planning Board) API
export const roadmapApi = {
  board: () => api.get('/roadmap/board'),
  create: (data) => api.post('/roadmap/cards', data),
  update: (id, data) => api.patch(`/roadmap/cards/${id}`, data),
  move: (id, data) => api.post(`/roadmap/cards/${id}/move`, data),
  delete: (id) => api.delete(`/roadmap/cards/${id}`),
};

// Note Templates API (reusable note/email snippets for the composer)
export const noteTemplatesApi = {
  list: (params) => api.get('/note-templates', { params }),
  get: (id) => api.get(`/note-templates/${id}`),
  create: (data) => api.post('/note-templates', data),
  update: (id, data) => api.put(`/note-templates/${id}`, data),
  remove: (id) => api.delete(`/note-templates/${id}`),
};

// Tax Rates API
export const taxRatesApi = {
  list: () => api.get('/tax-rates'),
  listActive: () => api.get('/tax-rates/active'),
  get: (id) => api.get(`/tax-rates/${id}`),
  create: (data) => api.post('/tax-rates', data),
  update: (id, data) => api.patch(`/tax-rates/${id}`, data),
  delete: (id) => api.delete(`/tax-rates/${id}`),
  reactivate: (id) => api.post(`/tax-rates/${id}/reactivate`),
  reorder: (order) => api.post('/tax-rates/reorder', order),
};

// Services API
export const servicesApi = {
  list: (params) => api.get('/services', { params }),
  listActive: () => api.get('/services/active'),
  listContractable: () => api.get('/services/contractable'),
  getTypes: () => api.get('/services/types'),
  get: (id) => api.get(`/services/${id}`),
  create: (data) => api.post('/services', data),
  update: (id, data) => api.patch(`/services/${id}`, data),
  delete: (id) => api.delete(`/services/${id}`),
  reactivate: (id) => api.post(`/services/${id}/reactivate`),
  reorder: (order) => api.post('/services/reorder', order),
  contractUsage: (id) => api.get(`/services/${id}/contract-usage`),
  syncToContracts: (id, data) => api.post(`/services/${id}/sync-to-contracts`, data),
};

// Billing - Contracts API
export const contractsApi = {
  // Bulk: set payment terms (days) on many contracts — future invoices only
  bulkPaymentTerms: (contractIds, paymentTerms) =>
    api.patch('/billing/contracts/bulk-payment-terms', { contractIds, paymentTerms }),
  list: (params) => api.get('/billing/contracts', { params }),
  get: (id) => api.get(`/billing/contracts/${id}`),
  create: (data) => api.post('/billing/contracts', data),
  update: (id, data) => api.patch(`/billing/contracts/${id}`, data),
  delete: (id) => api.delete(`/billing/contracts/${id}`),
  reorderServices: (id, serviceIds) => api.post(`/billing/contracts/${id}/reorder-services`, { serviceIds }),
  getLinkedAssets: (id) => api.get(`/billing/contracts/${id}/linked-assets`),
  report: () => api.get('/billing/contracts-report'),
  syncLines: (data) => api.post('/billing/contracts-sync-lines', data),
  resolveLineDescription: (contractId, serviceId) => api.get(`/billing/contracts/${contractId}/lines/${serviceId}/resolved-description`),
  // What un-invoiced billable work a PROPOSED fee coverage would make no-charge
  coverageImpact: (contractId, data) => api.post(`/billing/contracts/${contractId}/coverage-impact`, data),
  // Agreed future price for one service line ("£68/hr from 1 Sep")
  scheduleRateChange: (contractId, serviceId, data) =>
    api.post(`/billing/contracts/${contractId}/services/${serviceId}/rate-change`, data),
  cancelRateChange: (contractId, serviceId, effectiveFrom) =>
    api.delete(`/billing/contracts/${contractId}/services/${serviceId}/rate-change/${effectiveFrom}`),
  // Spread Charges (prior-balance recovery instalments)
  listSpreadCharges: (contractId) => api.get(`/billing/contracts/${contractId}/spread-charges`),
  createSpreadCharge: (contractId, data) => api.post(`/billing/contracts/${contractId}/spread-charges`, data),
  cancelSpreadCharge: (contractId, chargeId) => api.delete(`/billing/contracts/${contractId}/spread-charges/${chargeId}`),
};

// Billing - Invoices API
export const invoicesApi = {
  list: (params) => api.get('/billing/invoices', { params }),
  // Rebuild a draft contract invoice with today's time and rules; it keeps its number.
  regenerate: (id) => api.post(`/billing/invoices/${id}/regenerate`),
  // Headline figures counted by the database over the whole book, never from the
  // rows the browser happened to fetch.
  stats: () => api.get('/billing/invoices/stats'),
  get: (id) => api.get(`/billing/invoices/${id}`),
  create: (data) => api.post('/billing/invoices', data),
  recalculateTotals: (id) => api.post(`/billing/invoices/${id}/recalculate-totals`),
  update: (id, data) => api.patch(`/billing/invoices/${id}`, data),
  delete: (id) => api.delete(`/billing/invoices/${id}`),
  // Reprice all draft invoices under a client's (new) VAT treatment
  repriceDraftInvoices: (clientId, billingEntityId) =>
    api.post(`/billing/clients/${clientId}/reprice-draft-invoices`, null,
      billingEntityId ? { params: { billingEntityId } } : undefined),
  // Bring HAL's totals into line with what Xero holds (Xero-side corrections)
  adoptXeroFigures: (id) => api.post(`/billing/invoices/${id}/adopt-xero-figures`),
  refreshFromXero: (id) => api.post(`/billing/invoices/${id}/refresh-from-xero`),
  fromTime: (clientId, timeEntryIds) => 
    api.post(`/billing/invoices/from-time?client_id=${clientId}`, timeEntryIds),
  // Batch create draft invoices from pending time entries
  batchFromPending: (data) => api.post('/billing/invoices/batch-from-pending', data),
  // Ad-hoc invoices
  createAdHoc: (data) => api.post('/billing/invoices/adhoc', data),
  // Credit notes
  createCreditNote: (invoiceId) => api.post(`/billing/invoices/${invoiceId}/credit-note`),
  // Correcting a SENT invoice in place, keeping what it said before (v1.298.0)
  revisable: (id) => api.get(`/billing/invoices/${id}/revisable`),
  revise: (id, data) => api.post(`/billing/invoices/${id}/revise`, data),
  // By project or ticket
  byProject: (projectId) => api.get('/billing/invoices', { params: { projectId } }),
  byTicket: (ticketId) => api.get('/billing/invoices', { params: { ticketId } }),
};

// Billing - Combined API
export const billingApi = {
  // When an anniversary line is next charged, by the engine's own rule (fix brief A1).
  getAnniversaryPreview: (contractId, params) =>
    api.get(`/billing/contracts/${contractId}/anniversary-preview`, { params }),
  // Invoice Preview
  getInvoicePreview: (contractId, billingDate, includeTime = true) => {
    const params = { include_time: includeTime };
    if (billingDate) params.billing_date = billingDate;
    return api.get(`/billing/contracts/${contractId}/invoice-preview`, { params });
  },
  // Create invoice from contract preview
  createInvoiceFromContract: (data) => api.post('/billing/invoices/from-contract', data),
  // Proration / quantity changes — these live here, not on billingReviewApi:
  // ChangeQuantityDialog and AssetSidebar call them via billingApi.
  changeServiceQuantity: (contractId, serviceId, data) =>
    api.post(`/billing/contracts/${contractId}/services/${serviceId}/change-quantity`, data),
  previewQuantityChange: (contractId, serviceId, data) =>
    api.post(`/billing/contracts/${contractId}/services/${serviceId}/preview-quantity-change`, data),
  getQuantityHistory: (contractId, serviceId) =>
    api.get(`/billing/contracts/${contractId}/services/${serviceId}/quantity-history`),
};

// Asset Relationships API
export const assetRelationshipsApi = {
  list: (assetId) => api.get(`/assets/${assetId}/relationships`),
  create: (assetId, data) => api.post(`/assets/${assetId}/relationships`, data),
  delete: (assetId, relationshipId) => 
    api.delete(`/assets/${assetId}/relationships/${relationshipId}`),
};

// Uploads API
export const uploadsApi = {
  uploadFile: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/uploads/file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadFiles: (files) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    return api.post('/uploads/files', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteFile: (fileId) => api.delete(`/uploads/file/${fileId}`),
  // Fetch an authenticated upload (e.g. video) as a blob so it can be played
  // from an in-memory object URL. `url` is the stored attachment url, which
  // already includes the `/api` prefix (the axios baseURL also has `/api`, so
  // strip the leading `/api`).
  fetchBlob: (url) => api.get(url.replace(/^\/api/, ''), { responseType: 'blob' }),
};

// Storage Admin API — admin storage usage analytics
export const storageAdminApi = {
  getStats: () => api.get('/storage/stats'),
  getOrphans: () => api.get('/storage/orphans'),
  getSnapshots: (limit = 30) => api.get('/storage/snapshots', { params: { limit } }),
  refreshScan: () => api.post('/storage/scan-refresh'),
  takeSnapshotNow: () => api.post('/storage/snapshot-now'),
  cleanup: (guardDays) => api.get('/storage/cleanup', { params: guardDays ? { guard_days: guardDays } : {} }),
  cleanupItems: (category, limit = 200) => api.get('/storage/cleanup/items', { params: { category, limit } }),
  sweep: (body) => api.post('/storage/cleanup/sweep', body),
  cleanupDownload: (path) => api.get('/storage/cleanup/download', { params: { path }, responseType: 'blob' }),
};

// System Settings API
export const dataExportApi = {
  status: () => api.get('/admin/data-export/status'),
  open: (hours) => api.post(`/admin/data-export/open?hours=${hours}`),
  close: () => api.post('/admin/data-export/close'),
};

export const systemSettingsApi = {
  get: () => api.get('/system-settings'),
  update: (data) => api.patch('/system-settings', data),
  getDetectedUrl: () => api.get('/system-settings/detected-url'),
  cleanupLogs: () => api.post('/system-settings/cleanup-logs'),
  cleanupXeroSyncLogs: () => api.post('/system-settings/cleanup-xero-sync-logs'),
  cleanupM365SyncLogs: () => api.post('/system-settings/cleanup-m365-sync-logs'),
  cleanupBackgroundTaskLogs: () => api.post('/system-settings/cleanup-background-task-logs'),
  cleanupNotifications: () => api.post('/system-settings/cleanup-notifications'),
  cleanupAll: () => api.post('/system-settings/cleanup-all'),
  getAuditStats: () => api.get('/system-settings/audit-stats'),
  getAllLogStats: () => api.get('/system-settings/all-log-stats'),
};

// SLA Settings API
export const slaSettingsApi = {
  get: () => api.get('/sla-settings'),
  update: (data) => api.patch('/sla-settings', data),
  addHoliday: (date) => api.post('/sla-settings/holidays', { date }),
  removeHoliday: (date) => api.delete(`/sla-settings/holidays/${date}`),
};

// Background Task Runs API
export const backgroundTaskRunsApi = {
  list: (params) => api.get('/background-task-runs', { params }),
  listTasks: () => api.get('/background-task-runs/tasks'),
  runTask: (taskName) => api.post(`/background-task-runs/run/${taskName}`),
};

// Asset Fields API (dynamic type-specific fields)
export const assetMigrationApi = {
  preview: (plan) => api.post('/asset-migration/preview', { plan }),
  apply: (plan, token, acknowledgeUndecided) => api.post('/asset-migration/apply', { plan, token, acknowledgeUndecided }),
  runs: () => api.get('/asset-migration/runs'),
  conflictsCsv: (id) => api.get(`/asset-migration/runs/${id}/conflicts.csv`, { responseType: 'blob' }),
  undo: (id) => api.post(`/asset-migration/runs/${id}/undo`),
  driftAssets: () => api.get('/asset-migration/drift/assets'),
};

export const assetFieldsApi = {
  list: (params) => api.get('/asset-fields', { params }),
  getByType: (assetType, assetCategory) => api.get(`/asset-fields/by-type/${assetType}`, { params: assetCategory ? { assetCategory } : {} }),
  create: (data) => api.post('/asset-fields', data),
  update: (id, data) => api.patch(`/asset-fields/${id}`, data),
  delete: (id) => api.delete(`/asset-fields/${id}`),
  usage: (id) => api.get(`/asset-fields/${id}/usage`),
  reorder: (fieldOrders, category) => api.post('/asset-fields/reorder', fieldOrders, { params: category ? { category } : {} }),
  saveCards: (category, cards) => api.put(`/asset-fields/cards/${category}`, { cards }),
  getSubcategories: (category) => api.get(`/asset-fields/subcategories/${category}`),
  saveSubcategories: (category, data) => api.put(`/asset-fields/subcategories/${category}`, data),
  assignCard: (category, assignments) => api.post('/asset-fields/assign-card', { category, assignments }),
  bulkUpdateAssignments: (updates) => api.post('/asset-fields/bulk-update-assignments', updates),
  bulkScope: (updates) => api.post('/asset-fields/scope', { updates }),
  copyToCategories: (payload) => api.post('/asset-fields/copy-to-categories', payload),
};

// Asset Configuration Audit Log (Phase 5 Wave B)
export const assetConfigAuditApi = {
  list: (params) => api.get('/asset-config/audit', { params }),
};


// Email API
export const emailApi = {
  // Failed sends / retry
  listFailedEmails: (params) => api.get('/email/failed', { params }),
  resendFailedEmails: (data) => api.post('/email/failed/resend', data),
  dismissFailedEmails: (data) => api.post('/email/failed/dismiss', data),
  restoreDismissedEmails: (data) => api.post('/email/failed/restore', data),
  // Mailboxes
  listMailboxes: () => api.get('/email/mailboxes'),
  getMailbox: (id) => api.get(`/email/mailboxes/${id}`),
  createMailbox: (data) => api.post('/email/mailboxes', data),
  updateMailbox: (id, data) => api.patch(`/email/mailboxes/${id}`, data),
  deleteMailbox: (id) => api.delete(`/email/mailboxes/${id}`),
  testMailbox: (id) => api.post(`/email/mailboxes/${id}/test`),
  // Omitting sinceMinutes makes Poll Now use the mailbox's own poll floor — the same
  // thing the background poll uses — instead of an arbitrary look-back window.
  pollMailbox: (id, sinceMinutes) => api.post(
    `/email/mailboxes/${id}/poll${sinceMinutes ? `?since_minutes=${sinceMinutes}` : ''}`),
  listMissedMail: (id, days = 7) => api.get(`/email/mailboxes/${id}/missed`, { params: { days } }),
  importMissedMail: (id, data) => api.post(`/email/mailboxes/${id}/missed/import`, data),
  dismissMissedMail: (id, data) => api.post(`/email/mailboxes/${id}/missed/dismiss`, data),
  
  // Settings
  getSettings: () => api.get('/email/settings'),
  updateSettings: (data) => api.patch('/email/settings', data),
  
  // Company Settings
  getCompanySettings: () => api.get('/email/company-settings'),
  updateCompanySettings: (data) => api.patch('/email/company-settings', data),
  
  // Templates
  listTemplates: () => api.get('/email/templates'),
  getTemplate: (id) => api.get(`/email/templates/${id}`),
  createTemplate: (data) => api.post('/email/templates', data),
  updateTemplate: (id, data) => api.patch(`/email/templates/${id}`, data),
  restoreTemplateDefault: (id) => api.post(`/email/templates/${id}/restore-default`),
  deleteTemplate: (id) => api.delete(`/email/templates/${id}`),
  seedTemplates: () => api.post('/email/templates/seed'),
  getTemplateStats: () => api.get('/email/templates/stats'),
  sendTemplateTest: (id) => api.post(`/email/templates/${id}/test`),
  
  // Attachments
  uploadAttachment: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/email/attachments/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getAttachment: (id) => api.get(`/email/attachments/${id}`),
  downloadAttachment: (id) => api.get(`/email/attachments/${id}/download`),
  deleteAttachment: (id) => api.delete(`/email/attachments/${id}`),
  
  // Review Queue
  getReviewQueue: (params) => api.get('/email/review-queue', { params }),
  getReviewItem: (id) => api.get(`/email/review-queue/${id}`),
  getReviewItemMessage: (id) => api.get(`/email/review-queue/${id}/message`),
  resolveReviewQueueItem: (id, data) => api.post(`/email/review-queue/${id}/resolve`, data),
  getReviewQueueStats: () => api.get('/email/review-queue/stats'),
  exportTriageData: (params) =>
    api.get('/email/review-queue/export', { params, responseType: 'blob', timeout: 180000 }),
  bulkResolveReviewQueue: (data) => api.post('/email/review-queue/bulk-resolve', data),
  getRoutingHealth: () => api.get('/email/routing-health'),
  listSenderRules: () => api.get('/email/sender-rules'),
  createSenderRule: (data) => api.post('/email/sender-rules', data),
  updateSenderRule: (id, data) => api.patch(`/email/sender-rules/${id}`, data),
  deleteSenderRule: (id) => api.delete(`/email/sender-rules/${id}`),
  previewSenderRule: (data) => api.post('/email/sender-rules/preview', data),
  applyRuleToBacklog: (id, data) => api.post(`/email/sender-rules/${id}/apply-backlog`, data),
  getRulesActivity: (params) => api.get('/email/sender-rules/activity', { params }),
  listCapturedCodes: (params) => api.get('/email/codes', { params }),
  claimCode: (id) => api.post(`/email/codes/${id}/claim`),
  sendCodeToReview: (id) => api.post(`/email/codes/${id}/to-review`),
  markCodeSender: (data) => api.post('/email/codes/senders', data),

  listMonitoringAlerts: (params) => api.get('/email/alerts', { params }),
  listAlertHosts: (params) => api.get('/email/alerts/hosts', { params }),
  linkAlertHost: (hostname, data) => api.post(`/email/alerts/hosts/${encodeURIComponent(hostname)}/link`, data),
  ignoreAlertHost: (hostname) => api.post(`/email/alerts/hosts/${encodeURIComponent(hostname)}/ignore`),
  markAlertSender: (data) => api.post('/email/alerts/senders', data),
  sendAlertToReview: (id) => api.post(`/email/alerts/${id}/to-review`),
  previewAlertDigest: (params) => api.get('/email/alerts/digest/preview', { params }),
  sendAlertDigestNow: () => api.post('/email/alerts/digest/send-now'),

  listRuleSuggestions: (params) => api.get('/email/rule-suggestions', { params }),
  generateRuleSuggestions: (data) => api.post('/email/rule-suggestions/generate', data),
  approveRuleSuggestion: (id, data) => api.post(`/email/rule-suggestions/${id}/approve`, data),
  binRuleSuggestion: (id) => api.post(`/email/rule-suggestions/${id}/bin`),
  getRuleEffectReport: (params) => api.get('/email/sender-rules/effect-report', { params }),
  listDismissedEmail: (params) => api.get('/email/dismissed', { params }),
  restoreDismissedEmail: (id) => api.post(`/email/dismissed/${id}/restore`),
  reprocessReviewItem: (id) => api.post(`/email/review-queue/${id}/reprocess`),
  linkReviewItemToTicket: (id, data) => api.post(`/email/review-queue/${id}/link-ticket`, data),
  
  // Blocked Senders
  getBlockedSenders: () => api.get('/email/blocked-senders'),
  addBlockedSender: (sender) => api.post('/email/blocked-senders', { sender }),
  removeBlockedSender: (sender) => api.delete(`/email/blocked-senders/${encodeURIComponent(sender)}`),
  
  // Inbound Processing
  testProcessEmail: (fromEmail, fromName, subject, bodyText) => 
    api.post(`/email/process-test?from_email=${encodeURIComponent(fromEmail)}&from_name=${encodeURIComponent(fromName || '')}&subject=${encodeURIComponent(subject)}&body_text=${encodeURIComponent(bodyText)}`),
  
  // Messages
  listMessages: (params) => api.get('/email/messages', { params }),
  getMessage: (id) => api.get(`/email/messages/${id}`),
  
  // Send & Preview
  previewEmail: (data) => api.post('/email/preview', data),
  sendEmail: (data) => api.post('/email/send', data),
};

// Client Rates API
export const clientRatesApi = {
  list: (clientId, includeExpired = false, serviceId = null) => {
    const params = { include_expired: includeExpired };
    if (serviceId) params.service_id = serviceId;
    return api.get(`/client-rates/client/${clientId}`, { params });
  },
  getHistory: (clientId, serviceId) => api.get(`/client-rates/client/${clientId}/service/${serviceId}/history`),
  checkBillingConfig: (clientId) => api.get(`/client-rates/client/${clientId}/billing-config-check`),
  getEffective: (clientId, serviceId, asOfDate = null, billingEntityId = null) => {
    const params = { service_id: serviceId };
    if (asOfDate) params.as_of_date = asOfDate;
    if (billingEntityId) params.billingEntityId = billingEntityId;
    return api.get(`/client-rates/client/${clientId}/effective`, { params });
  },
  create: (data) => api.post('/client-rates', data),
  update: (id, data) => api.patch(`/client-rates/${id}`, data),
  scheduleChange: (id, data) => api.post(`/client-rates/${id}/schedule-change`, data),
  delete: (id) => api.delete(`/client-rates/${id}`),
  ensure: (clientId, serviceId, billingEntityId) =>
    api.post(`/client-rates/ensure/${clientId}/${serviceId}`,
      null, { params: billingEntityId ? { billingEntityId } : {} }),
};

// Billing Review API
export const billingReviewApi = {
  list: (params) => api.get('/billing/reviews', { params }),
  listIds: (params) => api.get('/billing/reviews/ids', { params }),
  get: (id) => api.get(`/billing/reviews/${id}`),
  update: (id, data) => api.patch(`/billing/reviews/${id}`, data),
  retry: (id) => api.post(`/billing/reviews/${id}/retry`),
  counts: (params) => api.get('/billing/reviews/counts', { params }),
  bulk: (data) => api.post('/billing/reviews/bulk', data),
  // Billing Runs
  listRuns: (params) => api.get('/billing/runs', { params }),
  triggerRun: (billingDate) => {
    const params = billingDate ? { billing_date: billingDate } : {};
    return api.post('/billing/run', null, { params });
  },
  // One-off monthly sweep (contracts with collectOneOffs + per-client ad-hoc)
  triggerMonthlySweep: ({ billingDate, clientId, contractId, dryRun = false } = {}) => {
    const params = { dry_run: dryRun };
    if (billingDate) params.billing_date = billingDate;
    if (clientId) params.client_id = clientId;
    if (contractId) params.contract_id = contractId;
    return api.post('/billing/sweep', null, { params });
  },
  revertBillableItem: (itemId) => api.post(`/billing/items/${itemId}/revert`),
  // Dashboard
  getDashboardStats: () => api.get('/billing/dashboard/stats'),
  getUpcomingBilling: (days = 30) => api.get('/billing/dashboard/upcoming', { params: { days } }),
  
  // Test/Simulation endpoints
  testContractBilling: (contractId, billingDate, dryRun = true) => 
    api.post(`/billing/test/contract/${contractId}`, null, { 
      params: { billing_date: billingDate, dry_run: dryRun } 
    }),
  testClientBilling: (clientId, billingDate, dryRun = true) => 
    api.post(`/billing/test/client/${clientId}`, null, { 
      params: { billing_date: billingDate, dry_run: dryRun } 
    }),
};

// Service Renewals API
export const serviceRenewalsApi = {
  list: (params) => api.get('/billing/service-renewals', { params }),
  renewService: (contractId, serviceId, termLengthMonths = null) => {
    const params = termLengthMonths ? { term_length_months: termLengthMonths } : {};
    return api.post(`/billing/service-renewals/${contractId}/${serviceId}/renew`, null, { params });
  },
};

// Retainer API
export const retainerApi = {
  // Terms
  listTerms: (params) => api.get('/retainers/terms', { params }),
  getTerm: (id) => api.get(`/retainers/terms/${id}`),
  createTerm: (data) => api.post('/retainers/terms', data),
  updateTerm: (id, data) => api.patch(`/retainers/terms/${id}`, data),
  
  // Hours Tracking
  logHours: (termId, hours, timeEntryId = null, year = null, month = null) => {
    const params = { hours };
    if (timeEntryId) params.timeEntryId = timeEntryId;
    if (year) params.year = year;
    if (month) params.month = month;
    return api.post(`/retainers/terms/${termId}/log-hours`, null, { params });
  },
  getUsage: (termId) => api.get(`/retainers/terms/${termId}/usage`),
  
  // Reconciliation
  getReconciliationPreview: (termId) => api.get(`/retainers/terms/${termId}/reconciliation-preview`),
  createReconciliation: (termId, notes = null) => {
    const params = notes ? { notes } : {};
    return api.post(`/retainers/terms/${termId}/reconcile`, null, { params });
  },
  settleTerm: (termId, data) => api.post(`/retainers/terms/${termId}/settle`, data),
  cancelTerm: (termId) => api.post(`/retainers/terms/${termId}/cancel`),
  getVariables: (termId) => api.get(`/retainers/terms/${termId}/variables`),
  
  // Dashboard
  getDashboard: (clientId = null) => {
    const params = clientId ? { clientId } : {};
    return api.get('/retainers/dashboard', { params });
  },
  getClientActiveRetainers: (clientId) => api.get(`/retainers/client/${clientId}/active`),
  getBurndown: () => api.get('/retainers/burndown'),
  getBurndownDetail: (termId) => api.get(`/retainers/burndown/${termId}`),
  
  // Backfill / Unbilled Time
  getUnbilledTime: (clientId, startDate, endDate) => 
    api.get('/retainers/unbilled-time', { params: { clientId, startDate, endDate } }),
  backfillTimeEntries: (termId, timeEntryIds) => 
    api.post(`/retainers/terms/${termId}/backfill`, timeEntryIds),
};

// Stock API
export const stockApi = {
  // Items
  list: (params, config = {}) => api.get('/stock', { params, ...config }),
  get: (id) => api.get(`/stock/${id}`),
  create: (data) => api.post('/stock', data),
  update: (id, data) => api.patch(`/stock/${id}`, data),
  delete: (id) => api.delete(`/stock/${id}`),
  
  // Quantity
  adjust: (id, data) => api.post(`/stock/${id}/adjust`, data),
  getHistory: (id) => api.get(`/stock/${id}/history`),
  
  // Queries
  getLowStock: () => api.get('/stock/low-stock'),
  getCategories: () => api.get('/stock/categories'),
  
  // Reports
  getSalesSummary: (params) => api.get('/stock/reports/sales-summary', { params }),
  getInventoryValue: (params) => api.get('/stock/reports/inventory-value', { params }),
  getMovementTrends: (params) => api.get('/stock/reports/movement-trends', { params }),
};

// Storage Layouts API
export const storageLayoutsApi = {
  list: () => api.get('/storage-layouts'),
  get: (id) => api.get(`/storage-layouts/${id}`),
  create: (data) => api.post('/storage-layouts', data),
  update: (id, data) => api.patch(`/storage-layouts/${id}`, data),
  delete: (id) => api.delete(`/storage-layouts/${id}`),
  addBlock: (id, block) => api.post(`/storage-layouts/${id}/blocks`, block),
  updateBlock: (id, blockId, data) => api.patch(`/storage-layouts/${id}/blocks/${blockId}`, data),
  deleteBlock: (id, blockId) => api.delete(`/storage-layouts/${id}/blocks/${blockId}`),
  updatePositions: (id, positions) => api.put(`/storage-layouts/${id}/blocks/positions`, positions),
  getBlockStats: (id) => api.get(`/storage-layouts/${id}/block-stats`),
};

// Orders API
export const suppliersApi = {
  list: (params) => api.get('/suppliers', { params }),
  create: (data) => api.post('/suppliers', data),
  refresh: () => api.post('/suppliers/refresh'),
  unmatched: () => api.get('/suppliers/unmatched'),
};

export const ordersApi = {
  // Orders CRUD
  list: (params) => api.get('/orders', { params }),
  get: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders', data),
  update: (id, data) => api.patch(`/orders/${id}`, data),
  delete: (id) => api.delete(`/orders/${id}`),
  getCount: () => api.get('/orders/count'),
  
  // Status transitions
  submit: (id) => api.post(`/orders/${id}/submit`),
  statusTransitions: () => api.get('/orders/status-transitions'),
  markOrdered: (id, params) => api.post(`/orders/${id}/mark-ordered`, null, { params }),
  cancel: (id, reason) => api.post(`/orders/${id}/cancel`, null, { params: { reason } }),
  
  // Order items
  addItem: (orderId, data) => api.post(`/orders/${orderId}/items`, data),
  updateItem: (orderId, itemId, data) => api.patch(`/orders/${orderId}/items/${itemId}`, data),
  removeItem: (orderId, itemId) => api.delete(`/orders/${orderId}/items/${itemId}`),
  
  // Goods-in / Receiving
  receive: (orderId, data) => api.post(`/orders/${orderId}/receive`, data),
  getPendingReceiving: () => api.get('/orders/pending-receiving'),
  getGoodsInDashboard: () => api.get('/orders/goods-in/dashboard'),
  
  // Storage locations
  listStorageLocations: (params) => api.get('/orders/storage-locations', { params }),
  createStorageLocation: (data) => api.post('/orders/storage-locations', data),
  updateStorageLocation: (id, data) => api.patch(`/orders/storage-locations/${id}`, data),
  deleteStorageLocation: (id) => api.delete(`/orders/storage-locations/${id}`),
  
  // User PIN (for goods-in authentication)
  setUserPin: (userId, pin) => api.post(`/orders/users/${userId}/pin`, { pin }),
  verifyPin: (userId, pin) => api.post('/orders/verify-pin', { userId, pin }),
  getGoodsInUsers: () => api.get('/orders/users/goods-in-list'),
  
  // Create asset from order item
  createAssetFromItem: (orderId, itemId, data) => api.post(`/orders/${orderId}/items/${itemId}/create-asset`, data),
};

// Order Requests API
export const orderRequestsApi = {
  list: (params) => api.get('/order-requests', { params }),
  get: (id) => api.get(`/order-requests/${id}`),
  create: (data) => api.post('/order-requests', data),
  update: (id, data) => api.patch(`/order-requests/${id}`, data),
  cancel: (id, reason) => api.post(`/order-requests/${id}/cancel`, null, { params: { reason } }),
  removeItem: (id, itemId) => api.delete(`/order-requests/${id}/items/${itemId}`),
  addItems: (id, items) => api.post(`/order-requests/${id}/add-items`, items),
  createPO: (id, data) => api.post(`/order-requests/${id}/create-po`, data),
  markExternal: (id, data) => api.post(`/order-requests/${id}/mark-external`, data),
  getCount: () => api.get('/order-requests/count'),
  resetItemLink: (id, itemId) => api.post(`/order-requests/${id}/items/${itemId}/reset-link`),
  reset: (id) => api.post(`/order-requests/${id}/reset`),
};

// Company Links API
export const companyLinksApi = {
  list: (clientId) => api.get(`/clients/${clientId}/company-links`),
  create: (clientId, data) => api.post(`/clients/${clientId}/company-links`, data),
  update: (clientId, linkId, data) => api.put(`/clients/${clientId}/company-links/${linkId}`, data),
  delete: (clientId, linkId) => api.delete(`/clients/${clientId}/company-links/${linkId}`),
  setPortalGrants: (clientId, linkId, data) =>
    api.put(`/clients/${clientId}/company-links/${linkId}/portal-grants`, data),
  portalVisibility: (clientId) => api.get(`/clients/${clientId}/portal-visibility`),
};

// Acts-on-behalf-of mandates — person-level authority over another company
export const portalMandatesApi = {
  forClient: (clientId) => api.get(`/clients/${clientId}/portal-mandates`),
  create: (clientId, data) => api.post(`/clients/${clientId}/portal-mandates`, data),
  update: (clientId, mandateId, data) =>
    api.put(`/clients/${clientId}/portal-mandates/${mandateId}`, data),
  revoke: (clientId, mandateId) =>
    api.delete(`/clients/${clientId}/portal-mandates/${mandateId}`),
};

// Internal Time API (non-billable internal activities)
export const internalTimeApi = {
  // Categories
  listCategories: (params) => api.get('/internal-time/categories', { params }),
  createCategory: (data) => api.post('/internal-time/categories', data),
  updateCategory: (id, data) => api.patch(`/internal-time/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/internal-time/categories/${id}`),
  
  // Time Entries
  listEntries: (params) => api.get('/internal-time/entries', { params }),
  getEntry: (id) => api.get(`/internal-time/entries/${id}`),
  createEntry: (data) => api.post('/internal-time/entries', data),
  updateEntry: (id, data) => api.patch(`/internal-time/entries/${id}`, data),
  deleteEntry: (id) => api.delete(`/internal-time/entries/${id}`),
  
  // Summary & Weekly
  getSummary: (params) => api.get('/internal-time/summary', { params }),
  getWeekly: (params) => api.get('/internal-time/weekly', { params }),
  getTeamWeekly: (params) => api.get('/internal-time/team-weekly', { params }),
};

// App Styling API
export const aiApi = {
  extractChat: (payload) => api.post('/ai/extract-chat', payload),
  getChatExtractSettings: () => api.get('/ai/chat-extract-settings'),
  getServiceSummaryPrompts: () => api.get('/ai/service-summary-prompts'),
  updateServiceSummaryPrompts: (payload) => api.put('/ai/service-summary-prompts', payload),
  resetServiceSummaryPrompts: () => api.post('/ai/service-summary-prompts/reset'),
  updateChatExtractSettings: (payload) => api.put('/ai/chat-extract-settings', payload),
};

export const changelogApi = {
  getFull: () => api.get('/changelog/full'),
};

export const serviceReviewApi = {
  getState: () => api.get('/service-review'),
  run: (monthly) => api.post('/service-review/run', { monthly }),
  getMarkdown: (id) => api.get(`/service-review/${id}/markdown`),
  download: (id) => api.get(`/service-review/${id}/download`, { responseType: 'blob' }),
  email: (id) => api.post(`/service-review/${id}/email`),
  remove: (id) => api.delete(`/service-review/${id}`),
};

export const appStylingApi = {
  get: () => api.get('/app-styling'),
  update: (data) => api.put('/app-styling', data),
  reset: () => api.post('/app-styling/reset'),
  getFonts: () => api.get('/app-styling/fonts'),
};

// Xero Integration API
export const xeroApi = {
  getSettings: () => api.get('/xero/settings'),
  updateSettings: (data) => api.patch('/xero/settings', data),
  getConnection: () => api.get('/xero/connection'),
  connect: () => api.post('/xero/connect'),
  disconnect: () => api.post('/xero/disconnect'),
  forceReset: () => api.post('/xero/force-reset'),
  fullDisconnect: () => api.post('/xero/full-disconnect'),
  debugRedirectUri: () => api.get('/xero/debug-redirect-uri'),
  diagnose: () => api.get('/xero/diagnose'),
  getSyncSummary: () => api.get('/xero/sync-summary'),
  syncInvoice: (invoiceId) => api.post(`/xero/sync-invoice/${invoiceId}`),
  stopPushing: (invoiceId) => api.post(`/xero/invoices/${invoiceId}/stop-pushing`),
  syncInvoicesBulk: (data) => api.post('/xero/sync-invoices-bulk', data),
  reconcile: () => api.post('/xero/reconcile'),
  getGlobalSyncMode: () => api.get('/xero/global-sync-mode'),
  getDeliveryDefault: () => api.get('/xero/delivery-default'),
  updateGlobalSyncMode: (data) => api.patch('/xero/global-sync-mode', data),
  // Contacts
  listContacts: () => api.get('/xero/contacts'),
  refreshContactsCache: () => api.post('/xero/contacts/refresh-cache'),
  searchContacts: (q, page) => api.get('/xero/contacts/search', { params: { q, page } }),
  getContact: (id) => api.get(`/xero/contacts/${id}`),
  // Pull from Xero
  getXeroAccounts: () => api.get('/xero/accounts'),
  getXeroTaxRates: () => api.get('/xero/tax-rates'),
  getXeroCurrencies: () => api.get('/xero/currencies'),
  // Polling
  pollStatuses: () => api.post('/xero/poll-statuses'),
  // Suppliers
  listSuppliers: (params) => api.get('/xero/suppliers', { params }),
  toggleSupplierVisibility: (id, data) => api.patch(`/xero/suppliers/${id}/visibility`, data),
  // Bill sync
  syncBill: (orderId) => api.post(`/xero/sync-bill/${orderId}`),
  // Credit note sync
  syncCreditNote: (invoiceId) => api.post(`/xero/sync-credit-note/${invoiceId}`),
  // What would go wrong if this invoice were pushed — asked BEFORE pushing (v1.298.0)
  pushPreflight: (invoiceId) => api.get(`/xero/push-preflight/${invoiceId}`),
  // Sync logs
  listSyncLogs: (params) => api.get('/xero/sync-logs', { params }),
  getSyncLogStats: (params) => api.get('/xero/sync-logs/stats', { params }),
  exportSyncLogs: (params) => api.get('/xero/sync-logs/export', { params, responseType: 'blob' }),
  getSyncLogDetail: (id) => api.get(`/xero/sync-logs/${id}`),
};


export const courierApi = {
  listForTicket: (ticketId) => api.get(`/courier-shipments/ticket/${ticketId}`),
  createForTicket: (ticketId, data) => api.post(`/courier-shipments/ticket/${ticketId}`, data),
  listForProject: (projectId) => api.get(`/courier-shipments/project/${projectId}`),
  createForProject: (projectId, data) => api.post(`/courier-shipments/project/${projectId}`, data),
  update: (id, data) => api.put(`/courier-shipments/${id}`, data),
  delete: (id) => api.delete(`/courier-shipments/${id}`),
  getEmailPrefill: (id) => api.get(`/courier-shipments/${id}/email-prefill`),
  updateStatus: (id, data) => api.put(`/courier-shipments/${id}/status`, data),
  listAwaitingReturn: () => api.get('/courier-shipments/awaiting-return'),
  getUnbilledShipments: (params) => api.get('/courier-shipments/billing/unbilled', { params }),
  generateInvoice: (data) => api.post('/courier-shipments/billing/generate-invoice', data),
  generateInvoicesBatch: (data) => api.post('/courier-shipments/billing/generate-invoices-batch', data),
};



// Billing Entities API
// Service Summaries — the client-facing "what your time covered" document
export const serviceSummariesApi = {
  queue: (params) => api.get('/billing/service-summaries/queue', { params }),
  log: (params) => api.get('/billing/service-summaries/log', { params }),
  generate: (data) => api.post('/billing/service-summaries/generate', data),
  get: (id) => api.get(`/billing/service-summaries/${id}`),
  update: (id, data) => api.patch(`/billing/service-summaries/${id}`, data),
  draft: (id, ticketId, kind = 'progress') =>
    api.post(`/billing/service-summaries/${id}/draft`, null, { params: { ticketId, kind } }),
  draftAllPreview: () => api.post('/billing/service-summaries/draft-all/preview'),
  draftAllStart: () => api.post('/billing/service-summaries/draft-all'),
  draftAllStatus: () => api.get('/billing/service-summaries/draft-all/status'),
  approve: (id) => api.post(`/billing/service-summaries/${id}/approve`),
  attachToXero: (id, replaceWhenPaid = false) =>
    api.post(`/billing/service-summaries/${id}/attach-to-xero`, null,
      { params: { replaceWhenPaid } }),
  pdf: (id) => api.get(`/billing/service-summaries/${id}/pdf`, { responseType: 'blob' }),
};

export const billingEntitiesApi = {
  list: (clientId) => api.get('/billing-entities', { params: { clientId } }),
  create: (data) => api.post('/billing-entities', data),
  update: (id, data) => api.put(`/billing-entities/${id}`, data),
  delete: (id) => api.delete(`/billing-entities/${id}`),
  getMembers: (entityId, clientId) => api.get(`/billing-entities/${entityId}/members`, { params: { clientId } }),
  reassign: (data) => api.post('/billing-entities/reassign', data),
  splitPreview: (clientId) => api.get('/billing-entities/split-preview', { params: { clientId } }),
  splitHistory: (params) => api.get('/billing-entities/split-history', { params }),
  dashboard: (params) => api.get('/billing-entities/dashboard', { params }),
  revenueTrend: (params) => api.get('/billing-entities/dashboard/revenue-trend', { params }),
  mappingReport: (params) => api.get('/billing-entities/dashboard/mapping-report', { params }),
};

// Statements — one page of account per billing company, in its own currency.
export const statementsApi = {
  client: (params) => api.get('/billing/statements/client', { params }),
  clientExport: (params) =>
    api.get('/billing/statements/client/export', { params, responseType: 'blob' }),
  entity: (entityId, params) => api.get(`/billing/statements/entity/${entityId}`, { params }),
  entityExport: (entityId, params) =>
    api.get(`/billing/statements/entity/${entityId}/export`, { params, responseType: 'blob' }),
};


// Dry Run — read-only preview of what a billing run would raise
export const dryRunApi = {
  candidates: (params) => api.get('/billing/dry-run/candidates', { params }),
  run: (data) => api.post('/billing/dry-run', data),
};


// Included Value — what each covering fee absorbed (internal only)
export const includedValueApi = {
  report: (params) => api.get('/billing/included-value', { params }),
  export: (params) =>
    api.get('/billing/included-value/export', { params, responseType: 'blob' }),
};


// Rate Change Warning — impact of a rate change on hours already logged
export const rateChangeApi = {
  impact: (data) => api.post('/billing/rate-change/impact', data),
  reprice: (data) => api.post('/billing/rate-change/reprice', data),
  timeline: (clientId) => api.get(`/billing/rate-change/timeline/${clientId}`),
  timelineClients: () => api.get('/billing/rate-change/timeline/clients'),
};


// Knowledge Base
export const searchApi = {
  search: (params) => api.get('/search', { params }),
};


export const workflowImportApi = {
  dryRun: (file, workflowCategoryId, sopCategoryId) => { const f = new FormData(); f.append('file', file); f.append('workflowCategoryId', workflowCategoryId); f.append('sopCategoryId', sopCategoryId); return api.post('/kb/workflow-import/dry-run', f, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  confirm: (id, data) => api.post(`/kb/workflow-import/${id}/confirm`, data),
  history: () => api.get('/kb/workflow-import/history'),
  exportFile: () => api.get('/kb/workflow-import/export', { responseType: 'blob' }),
  setClientCode: (clientId, code) => api.put(`/clients/${clientId}/code`, { code }),
};

export const buildValuesApi = {
  keys: () => api.get('/build-value-keys'),
  createKey: (data) => api.post('/build-value-keys', data),
  updateKey: (key, data) => api.patch(`/build-value-keys/${key}`, data),
  deleteKey: (key) => api.delete(`/build-value-keys/${key}`),
  forClient: (clientId) => api.get(`/clients/${clientId}/build-values`),
  summary: (clientId) => api.get(`/clients/${clientId}/build-values/summary`),
  set: (clientId, key, data) => api.put(`/clients/${clientId}/build-values/${key}`, data),
  history: (clientId, key) => api.get(`/clients/${clientId}/build-values/${key}/history`),
  reveal: (clientId, key, entityId, profileId) => api.post(`/clients/${clientId}/build-values/${key}/reveal`, { entityId: entityId || undefined, profileId: profileId || undefined }),
  createProfile: (clientId, data) => api.post(`/clients/${clientId}/build-profiles`, data),
  renameProfile: (clientId, profileId, data) => api.patch(`/clients/${clientId}/build-profiles/${profileId}`, data),
  deleteProfile: (clientId, profileId) => api.delete(`/clients/${clientId}/build-profiles/${profileId}`),
  variations: (clientId) => api.get(`/clients/${clientId}/workflow-variations`),
  createVariation: (clientId, data) => api.post(`/clients/${clientId}/workflow-variations`, data),
  updateVariation: (clientId, id, data) => api.put(`/clients/${clientId}/workflow-variations/${id}`, data),
  deleteVariation: (clientId, id) => api.delete(`/clients/${clientId}/workflow-variations/${id}`),
  variationTargets: (workflowId) => api.get(`/kb/workflows/${workflowId}/variation-targets`),
  previewVariation: (clientId, data) => api.post(`/clients/${clientId}/workflow-variations/preview`, data),
  workflowClientChanges: (workflowId) => api.get(`/kb/workflows/${workflowId}/client-changes`),
  workflowClientPicker: (workflowId, params) => api.get(`/kb/workflows/${workflowId}/client-picker`, { params }),
  workflowClientView: (workflowId, clientId) => api.get(`/kb/workflows/${workflowId}/client-view`, { params: { clientId } }),
  clientCopySop: (sopId, clientId) => api.post(`/kb/sops/${sopId}/client-copy`, { clientId }),
};

export const kbApi = {
  // Categories
  listCategories: (params) => api.get('/kb/categories', { params }),
  createCategory: (data) => api.post('/kb/categories', data),
  updateCategory: (id, data) => api.patch(`/kb/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/kb/categories/${id}`),
  reorderCategories: (items) => api.put('/kb/categories/reorder', { items }),

  // Workflow categories (separate collection)
  listWorkflowCategories: (params) => api.get('/kb/workflow-categories', { params }),
  createWorkflowCategory: (data) => api.post('/kb/workflow-categories', data),
  updateWorkflowCategory: (id, data) => api.patch(`/kb/workflow-categories/${id}`, data),
  deleteWorkflowCategory: (id) => api.delete(`/kb/workflow-categories/${id}`),
  reorderWorkflowCategories: (items) => api.put('/kb/workflow-categories/reorder', { items }),

  // Articles
  listArticles: (params) => api.get('/kb/articles', { params }),
  getArticle: (id) => api.get(`/kb/articles/${id}`),
  createArticle: (data) => api.post('/kb/articles', data),
  updateArticle: (id, data) => api.patch(`/kb/articles/${id}`, data),
  reviewArticle: (id) => api.post(`/kb/articles/${id}/review`),
  deleteArticle: (id) => api.delete(`/kb/articles/${id}`),
  archiveArticle: (id) => api.post(`/kb/articles/${id}/archive`),
  restoreArticle: (id) => api.post(`/kb/articles/${id}/restore`),
  duplicateWorkflow: (id) => api.post(`/kb/articles/${id}/duplicate`),
  workflowUsage: (id) => api.get(`/kb/articles/${id}/workflow-usage`),
  workflowBuilderSops: (workflowId) => api.get(`/kb/workflows/${workflowId || 'new'}/sops`),

  // Versions
  listVersions: (articleId) => api.get(`/kb/articles/${articleId}/versions`),
  revertVersion: (articleId, versionNum) => api.post(`/kb/articles/${articleId}/revert/${versionNum}`),
  saveWorkflowDraft: (id, data) => api.put(`/kb/workflows/${id}/draft`, data),
  discardWorkflowDraft: (id) => api.delete(`/kb/workflows/${id}/draft`),
  publishWorkflow: (id, data) => api.post(`/kb/workflows/${id}/publish`, data),
  saveSopDraft: (id, data) => api.put(`/kb/sops/${id}/draft`, data),
  flattenSopDraft: (id) => api.post(`/kb/sops/${id}/flatten-draft`),
  trySop: (id, data) => api.post(`/kb/sops/${id}/try`, data),
  discardSopDraft: (id) => api.delete(`/kb/sops/${id}/draft`),
  publishSop: (id, data) => api.post(`/kb/sops/${id}/publish`, data),

  // Linking
  articlesByLink: (linkType, linkId) => api.get('/kb/articles/by-link', { params: { linkType, linkId } }),
  articlesForClient: (clientId) => api.get(`/kb/articles/for-client/${clientId}`),
  linkedDetails: (articleId) => api.get(`/kb/articles/${articleId}/linked-details`),
  downloadArticlePdf: (articleId, params) =>
    api.get(`/kb/articles/${articleId}/pdf`, { params, responseType: 'blob', timeout: 90000 }),
  related: (articleId) => api.get(`/kb/articles/${articleId}/related`),
  connectivity: (params) => api.get('/kb/connectivity', { params }),

  // Checklist Runs
  listRuns: (params) => api.get('/kb/checklist-runs', { params }),
  getRun: (runId) => api.get(`/kb/checklist-runs/${runId}`),
  createRun: (data) => api.post('/kb/checklist-runs', data),
  updateRun: (runId, data) => api.patch(`/kb/checklist-runs/${runId}`, data),
  updateRunStep: (runId, stepId, data) => api.patch(`/kb/checklist-runs/${runId}/steps/${encodeURIComponent(stepId)}`, data),
  recordRunValue: (runId, stepId, data) => api.post(`/kb/checklist-runs/${runId}/steps/${encodeURIComponent(stepId)}/record`, data),
  deleteRun: (runId) => api.delete(`/kb/checklist-runs/${runId}`),

  // Workflows
  listCustomerOverrides: (workflowId, params) => api.get(`/kb/workflows/${workflowId}/customer-overrides`, { params }),
  upsertCustomerOverride: (workflowId, clientId, data) => api.put(`/kb/workflows/${workflowId}/customer-overrides/${clientId}`, data),
  deleteCustomerOverride: (workflowId, clientId, params) => api.delete(`/kb/workflows/${workflowId}/customer-overrides/${clientId}`, { params }),
  assembleWorkflow: (workflowId, params) => api.get(`/kb/workflows/${workflowId}/assemble`, { params }),
  startWorkflowRun: (workflowId, data) => api.post(`/kb/workflows/${workflowId}/start-run`, data),
  previewWorkflow: (workflowId, data) => api.post(`/kb/workflows/${workflowId}/preview`, data),
  workflowOpenRuns: (workflowId) => api.get(`/kb/workflows/${workflowId}/open-runs`),
  oneOffMigrations: () => api.get('/admin/one-off-migrations'),
  sopConversionDryRun: (params) => api.get('/admin/sop-conversion/dry-run', { params }),
  sopConversionApply: (data) => api.post('/admin/sop-conversion/apply', data),
  conditionSources: (articleId) => api.get(`/kb/articles/${articleId}/condition-sources`),
  updateRunStartAnswers: (runId, data) => api.patch(`/kb/checklist-runs/${runId}/start-answers`, data),
  updateRunContext: (runId, data) => api.patch(`/kb/checklist-runs/${runId}/context`, data),
  setRunQc: (runId, data) => api.patch(`/kb/checklist-runs/${runId}/qc`, data),
  setTicketQc: (ticketId, data) => api.post(`/kb/tickets/${ticketId}/qc`, data),
  runDefaults: (ticketId, params) => api.get(`/kb/tickets/${ticketId}/run-defaults`, { params }),
  changeRunOwner: (runId, data) => api.patch(`/kb/checklist-runs/${runId}/owner`, data),
  addRunStepPhoto: (runId, stepId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/kb/checklist-runs/${runId}/steps/${encodeURIComponent(stepId)}/photos`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteRunPhoto: (runId, photoId) => api.delete(`/kb/checklist-runs/${runId}/photos/${photoId}`),
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/kb/upload-image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  exportArticle: (id) => api.get(`/kb/articles/${id}/export`),
  importArticle: (data) => api.post('/kb/articles/import', data),

  // Seed
  seed: () => api.post('/kb/seed'),
};


export const completionRulesApi = {
  get: () => api.get('/completion-rules'),
  update: (data) => api.put('/completion-rules', data),
  validate: (ticketId) => api.post(`/completion-rules/validate/${ticketId}`),
};


export const dashboardApi = {
  stats: () => api.get('/dashboard/stats'),
  teamWorkload: (internal = false) => api.get('/dashboard/team-workload', { params: { internal } }),
  teamWorkloadProjects: (internal = false) => api.get('/dashboard/team-workload-projects', { params: { internal } }),
  upcomingSchedule: (userId = 'all') => api.get('/dashboard/upcoming-schedule', { params: { userId } }),
  assetHealth: () => api.get('/dashboard/asset-health'),
};

export const healthzApi = {
  get: () => api.get('/healthz'),
};

export const followingApi = {
  list: (params = {}) => api.get('/following', { params }),
  markSeen: (entityType, entityId) => api.post('/following/seen', { entityType, entityId }),
  markAllSeen: () => api.post('/following/seen-all'),
  unfollow: (items) => api.post('/following/unfollow', { items }),
};

export const systemAlertsApi = {
  list: (params) => api.get('/system-alerts', { params }),
  summary: () => api.get('/system-alerts/summary'),
  acknowledge: (id) => api.post(`/system-alerts/${id}/acknowledge`),
  resolve: (id) => api.post(`/system-alerts/${id}/resolve`),
};

// Alert routing settings (admin) — channels, per-severity email, digest, mutes
export const alertSettingsApi = {
  get: () => api.get('/alert-settings'),
  update: (data) => api.put('/alert-settings', data),
  sendTest: (severity) => api.post('/alert-settings/test', { severity }),
};


// Client Custom Fields API
export const clientFieldsApi = {
  // Card CRUD
  listCards: () => api.get('/client-fields/cards'),
  createCard: (data) => api.post('/client-fields/cards', data),
  updateCard: (cardId, data) => api.put(`/client-fields/cards/${cardId}`, data),
  deleteCard: (cardId) => api.delete(`/client-fields/cards/${cardId}`),
  reorderCards: (items) => api.post('/client-fields/cards/reorder', { items }),
  // Field CRUD
  addField: (cardId, data) => api.post(`/client-fields/cards/${cardId}/fields`, data),
  updateField: (cardId, fieldId, data) => api.put(`/client-fields/cards/${cardId}/fields/${fieldId}`, data),
  deleteField: (cardId, fieldId) => api.delete(`/client-fields/cards/${cardId}/fields/${fieldId}`),
  fieldUsage: (cardId, fieldId) => api.get(`/client-fields/cards/${cardId}/fields/${fieldId}/usage`),
  workflowUsage: () => api.get('/client-fields/workflow-usage'),
  cardUsage: (cardId) => api.get(`/client-fields/cards/${cardId}/usage`),
  moveField: (cardId, fieldId, targetCardId) => api.post(`/client-fields/cards/${cardId}/fields/${fieldId}/move`, { targetCardId }),
  reorderFields: (cardId, items) => api.post(`/client-fields/cards/${cardId}/fields/reorder`, { items }),
  // Client values
  getValues: (clientId) => api.get(`/client-fields/clients/${clientId}/values`),
  saveValues: (clientId, values) => api.put(`/client-fields/clients/${clientId}/values`, { values }),
  revealSecret: (clientId, fieldId) => api.post(`/client-fields/clients/${clientId}/secrets/${encodeURIComponent(fieldId)}/reveal`),
};


// Portal Admin API — invite management
export const portalAdminApi = {
  sendInvite: (data) => api.post('/portal/admin/invite', data),
  listInvites: (params = {}) => api.get('/portal/admin/invites', { params }),
  revokeInvite: (inviteId) => api.delete(`/portal/admin/invites/${inviteId}`),
  getClientBranding: (clientId) => api.get(`/portal/admin/settings/client/${clientId}`),
  updateClientBranding: (clientId, data) => api.put(`/portal/admin/settings/client/${clientId}`, data),
};


// Site Visits API — "Awaiting Site Visit" flag + bundling queue
export const siteVisitsApi = {
  flag: (entityType, id, data = {}) => api.post(`/site-visits/${entityType}/${id}/flag`, data),
  update: (entityType, id, data = {}) => api.patch(`/site-visits/${entityType}/${id}`, data),
  complete: (entityType, id) => api.post(`/site-visits/${entityType}/${id}/complete`),
  clear: (entityType, id) => api.post(`/site-visits/${entityType}/${id}/clear`),
  summary: (params = {}) => api.get('/site-visits/summary', { params }),
  queue: (params = {}) => api.get('/site-visits/queue', { params }),
  candidates: (siteId, params = {}) => api.get(`/site-visits/candidates/${siteId}`, { params }),
};




// PDF Jobs API — a render runs in the background and only the finished file is
// fetched, so no request is held open for the length of a headless render.
export const pdfJobsApi = {
  create: (data) => api.post('/pdf-jobs', data),
  get: (jobId) => api.get(`/pdf-jobs/${jobId}`),
};
